/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import DeckViewer, { CombinedSource } from '../components/DeckViewer';
import CombineDeckModal, { deckableCardsFromBoard } from '../components/CombineDeckModal';

const API = 'https://coast-terminal-production.up.railway.app';

// ─── SVG tool icons (clean, no unicode glyph rendering surprises) ─────

const ICON_SELECT = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round">
    <path d="M5 3l14 9-6 1.5-2.5 6-5.5-16.5z" />
  </svg>
);
const ICON_STICKY = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <rect x="4" y="4" width="16" height="16" rx="3" />
  </svg>
);
const ICON_TEXT = (
  <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 17, fontWeight: 700 }}>T</span>
);
const ICON_LINK = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 007 0l3-3a5 5 0 00-7-7l-1 1" />
    <path d="M14 11a5 5 0 00-7 0l-3 3a5 5 0 007 7l1-1" />
  </svg>
);
const ICON_SECTION = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <line x1="3" y1="9" x2="21" y2="9" />
  </svg>
);
const ICON_TRASH = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="6" y1="18" x2="18" y2="6" />
  </svg>
);

// Freeform-style infinite board. Single-user, persisted to localStorage.
// Phase 1: sticky notes, text boxes, Coast section cards.
// Phase 2 (later): drawings, PDFs, photos, multi-user sync.

interface Props {
  token: string;
  onExitToDashboard: () => void;
  onOpenMap?: () => void;
}

type CardType = 'sticky' | 'text' | 'section' | 'link' | 'image' | 'pdf' | 'live' | 'chart' | 'voice' | 'voice-table' | 'meeting';

interface VoiceMemoRow {
  id:           string;
  recorded_at:  string;
  transcript:   string;
  extracted: {
    property_name?:     string | null;
    location?:          string | null;
    address?:           string | null;
    property_type?:     string | null;
    units?:             number | null;
    year_built?:        number | null;
    purchase_price?:    number | null;
    rent_monthly?:      number | null;
    cap_rate?:          number | null;
    noi_monthly?:       number | null;
    cash_flow_monthly?: number | null;
    sqft?:              number | null;
    amenities?:         string[];
    issues?:            string[];
    notes?:             string[];
    summary?:           string;
  } | null;
  // Verbatim-only entity extraction — populated by /me/voice-parse on
  // memos parsed in the same Claude round-trip as the structured CRE
  // fields above. Older memos (parsed before this combined response
  // shipped) leave this undefined; the EntityChips component falls
  // back to /me/extract-entities for those.
  entities?: {
    addresses:   string[];
    properties:  string[];
    tribes:      string[];
    regulations: string[];
    people:      string[];
    money:       string[];
  };
  parse_error?: string;
}
type Theme = 'light' | 'dark';
type LiveKind = 'market-snapshot' | 'peer-rank' | 'property-card';
type ChartKind = 'rent-bar' | 'amenity-bar' | 'rent-vs-sqft' | 'persona-rank';

interface BoardCard {
  id: string;
  type: CardType;
  x: number;        // canvas coords
  y: number;
  w: number;
  h: number;
  color?: string;   // sticky note color id
  text?: string;
  section?: string; // for section link cards: 'markets' | 'peers' | 'properties' | 'searcher'
  href?: string;    // for link / image / pdf cards
  title?: string;
  // Live data cards
  liveKind?: LiveKind;
  msaCbsa?: string;     // for 'market-snapshot'
  personaId?: string;   // for 'peer-rank'
  rowIndex?: number;    // for 'property-card'
  sheetId?: number;     // for property/peer
  // Chart cards
  chartKind?: ChartKind;
  chartConfig?: {
    sheetId?: number;
    personaId?: string;
    unitType?: 'studio' | '1b1b' | '2b2b';
  };
  // Voice memo cards (single memo)
  voice?: VoiceMemoRow & { recorded_at: string };
  // Voice-table cards: a growing spreadsheet of memos
  voiceTable?: {
    title: string;
    rows:  VoiceMemoRow[];
  };
  // Sit-in meeting cards — produced by the global FAB recorder.
  // The transcript and Claude-parsed digest are stored in the card
  // itself; the board's existing auto-save persists everything via
  // /boards. No new backend table needed.
  meeting?: {
    recorded_at:  string;
    duration_sec: number;
    transcript:   string;
    parsed: {
      summary:        string;
      speakers:       Array<{
        id: string; name?: string | null;
        role_guess?: string | null;
        confidence: 'high' | 'medium' | 'low';
        share_pct: number;
      }>;
      topics:         Array<{ label: string; summary: string; anchor: string }>;
      decisions:      Array<{ decision: string; quote: string }>;
      action_items:   Array<{ action: string; owner: string | null; deadline: string | null; quote: string }>;
      open_questions: Array<{ question: string; quote: string }>;
      risks_flagged:  Array<{ risk: string; quote: string }>;
      entities: {
        addresses: string[]; properties: string[]; tribes: string[];
        regulations: string[]; people: string[]; money: string[];
      };
    } | null;
  };
}

interface BoardState {
  cards: BoardCard[];
  viewport: { tx: number; ty: number; scale: number };
  theme: Theme;
}

const STICKY_COLORS = [
  { id: 'yellow', light: '#FEF3C7', dark: '#7C5E10', text: '#1F2937' },
  { id: 'pink',   light: '#FCE7F3', dark: '#7E2952', text: '#1F2937' },
  { id: 'blue',   light: '#DBEAFE', dark: '#1E3A8A', text: '#FFFFFF' },
  { id: 'green',  light: '#D1FAE5', dark: '#065F46', text: '#FFFFFF' },
  { id: 'orange', light: '#FED7AA', dark: '#7C2D12', text: '#FFFFFF' },
  { id: 'purple', light: '#DDD6FE', dark: '#4C1D95', text: '#FFFFFF' },
];

const SECTIONS = [
  { id: 'markets',    label: 'Markets',    desc: 'Per-MSA macro stack from primary sources' },
  { id: 'peers',      label: 'Peers',      desc: 'Persona-driven building comparison' },
  { id: 'properties', label: 'Properties', desc: 'Your sheet, rankings, niches' },
  { id: 'searcher',   label: 'Searcher',   desc: 'US map + state CRE data' },
  { id: 'reading',    label: 'Read Order', desc: 'Sequenced curriculum + journal' },
  { id: 'glossary',   label: 'Glossary',   desc: 'Multifamily terms with formulas' },
];

// ─── Persistence ──────────────────────────────────────────────────────────
// Boards now live in the backend (`/boards` router). Each user can have
// many named boards, and the entire state {cards, viewport, theme} is
// stored as a JSON blob keyed by board id.
//
// localStorage is kept as a *cache only*:
//   coast.board.current_id   — id of the last-opened board (per-user via token)
//   coast.board.cache.{id}   — last-known state JSON, used while the GET races
//   coast.board.draft.legacy — pre-multi-board localStorage that we migrate
//                              into the user's first real board on first boot
// This guarantees zero data loss across reloads / tab switches: even if the
// network is slow, we render from the cache instantly and reconcile on
// fetch.

const LS_LEGACY_KEY = 'coast.board.v1';
const LS_CURRENT_ID = 'coast.board.current_id';
const LS_CACHE_PREFIX = 'coast.board.cache.';

function genId(): string {
  return Math.random().toString(36).slice(2, 9);
}

function emptyBoardState(): BoardState {
  return {
    cards: [
      {
        id: genId(), type: 'sticky', x: 200, y: 200, w: 220, h: 140,
        color: 'yellow',
        text: "Welcome to your new Coast Board.\n\nDrag cards anywhere.\nDrop sticky notes, text, or Coast sections.\nPan = drag empty space.\nZoom = Cmd / Ctrl + scroll.",
      },
      {
        id: genId(), type: 'section', section: 'peers',
        x: 460, y: 200, w: 240, h: 140,
        title: 'Peers',
      },
    ],
    viewport: { tx: 0, ty: 0, scale: 1 },
    theme: 'light',
  };
}

function readCachedState(boardId: number): BoardState | null {
  try {
    const raw = localStorage.getItem(LS_CACHE_PREFIX + boardId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BoardState;
    if (parsed && parsed.cards && parsed.viewport) return parsed;
  } catch { /* ignore */ }
  return null;
}

function writeCachedState(boardId: number, s: BoardState) {
  try { localStorage.setItem(LS_CACHE_PREFIX + boardId, JSON.stringify(s)); }
  catch { /* ignore */ }
}

/* Drain every coast.meeting.pending.* localStorage entry and merge each
 * one into the given BoardState as a new meeting card. Idempotent —
 * the LS keys are removed as they're consumed.
 *
 * Called inside openBoard right after the backend's saved state lands,
 * so the merge sees the user's actual board (not an empty initial
 * state) and isn't subsequently overwritten by a slow async fetch. */
function mergePendingMeetings(s: BoardState): BoardState {
  const cards = [...s.cards];
  let added = 0;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('coast.meeting.pending.')) keys.push(k);
    }
    for (const k of keys) {
      try {
        const raw = localStorage.getItem(k);
        if (!raw) { localStorage.removeItem(k); continue; }
        const m = JSON.parse(raw);
        // Lay the cards out in a small cascade near (200, 200) — guarantees
        // they land in view regardless of the current viewport pan, which
        // was the failure mode of the previous on-mount placement that
        // depended on viewport coords.
        cards.push({
          id:   Math.random().toString(36).slice(2, 9),
          type: 'meeting',
          x:    200 + added * 24,
          y:    200 + added * 24,
          w:    440, h: 320,
          meeting: m,
        });
        added += 1;
      } catch { /* ignore individual entry parse failures */ }
      finally { localStorage.removeItem(k); }
    }
  } catch { /* swallow — best-effort merge */ }
  return added > 0 ? { ...s, cards } : s;
}

function readLegacyState(): BoardState | null {
  try {
    const raw = localStorage.getItem(LS_LEGACY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BoardState;
    if (parsed && parsed.cards && parsed.viewport) return parsed;
  } catch { /* ignore */ }
  return null;
}

interface BoardSummary {
  id: number;
  title: string;
  card_count: number;
  updated_at: string | null;
  created_at: string | null;
}

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!t || Number.isNaN(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 5)     return 'just now';
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// ─── Component ──────────────────────────────────────────────────────────

export default function Board({ token, onExitToDashboard, onOpenMap }: Props) {
  // ─── Multi-board persistence ─────────────────────────────────────────
  // boardId is null until we've identified or created the user's active
  // board. While null, we render an empty canvas + "Loading…" status.
  const [boardId, setBoardId] = useState<number | null>(null);
  const [boardTitle, setBoardTitle] = useState<string>('Untitled board');
  const [boardsList, setBoardsList] = useState<BoardSummary[]>([]);
  const [bootError, setBootError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [renamingTitle, setRenamingTitle] = useState<string | null>(null);

  // Start with an empty shell; real state is filled in by the boot effect.
  const [state, setState] = useState<BoardState>(() => ({
    cards: [], viewport: { tx: 0, ty: 0, scale: 1 }, theme: 'light',
  }));
  const [tool, setTool] = useState<'select' | 'sticky' | 'text' | 'link'>('select');
  const [selected, setSelected] = useState<string | null>(null);
  const [pendingColor, setPendingColor] = useState<string>('yellow');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  // Combine-deck modal state: open the picker, then when user confirms,
  // we open the DeckViewer with `combined={picks}`.
  const [combineOpen, setCombineOpen] = useState(false);
  const [combinedSources, setCombinedSources] = useState<CombinedSource[] | null>(null);
  // When user arrives via the RecentMeetingPill (URL `/board?focus=<id>`),
  // we highlight the newest meeting card for a few seconds + scroll the
  // viewport to centre it. The id in the URL is the SAVE marker id, not
  // the card id — but "newest meeting card by recorded_at" is the right
  // target by construction.
  const [flashCardId, setFlashCardId] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  // Track whether we're done with the initial fetch so the auto-save
  // effect doesn't fire on the empty bootstrap state.
  const bootedRef = useRef(false);
  const saveTimer = useRef<number | null>(null);
  const lastSavedSnapshot = useRef<string>('');

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  // ── Helpers ────────────────────────────────────────────────────────
  const refreshBoardsList = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/boards/list`, authHeader);
      const list: BoardSummary[] = (r.data?.boards || []) as BoardSummary[];
      setBoardsList(list);
      return list;
    } catch {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openBoard = useCallback(async (id: number) => {
    // Render instantly from local cache if we have one, then fetch fresh.
    const cached = readCachedState(id);
    if (cached) {
      setState(mergePendingMeetings(cached));
      setBoardId(id);
      bootedRef.current = true;
    }
    try {
      const r = await axios.get(`${API}/boards/${id}`, authHeader);
      const fresh = r.data?.state as BoardState | undefined;
      const title = r.data?.title || 'Untitled board';
      if (fresh && fresh.cards && fresh.viewport) {
        // Merge any pending meetings (from coast.meeting.pending.* keys
        // in localStorage) into the freshly-loaded state. This is the
        // correct moment to do it: doing it earlier was getting nuked
        // by this very fetch overwriting state. The merged state is
        // then written back to cache + (via the auto-save effect)
        // persisted to /boards.
        const merged = mergePendingMeetings(fresh);
        setState(merged);
        writeCachedState(id, merged);
        // We deliberately do NOT seed lastSavedSnapshot with `merged` —
        // we want the auto-save effect to see this as a dirty diff
        // against `fresh` (the server's view) so it persists.
        lastSavedSnapshot.current = JSON.stringify(fresh);
      }
      setBoardTitle(title);
      setBoardId(id);
      setLastSavedAt(r.data?.updated_at || null);
      localStorage.setItem(LS_CURRENT_ID, String(id));
      setSaveStatus('saved');
      bootedRef.current = true;
    } catch (e: any) {
      // Token expired or board missing: drop back to the picker.
      setBootError(e?.response?.data?.detail || 'Could not load board.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const createBoard = useCallback(async (
    title: string,
    seedState?: BoardState,
  ): Promise<BoardSummary | null> => {
    try {
      const r = await axios.post(`${API}/boards`, {
        title: title || 'Untitled board',
        state: seedState || emptyBoardState(),
      }, authHeader);
      const created = r.data as { id: number; title: string; state: BoardState };
      await refreshBoardsList();
      if (created.state) {
        setState(created.state);
        writeCachedState(created.id, created.state);
        lastSavedSnapshot.current = JSON.stringify(created.state);
      }
      setBoardTitle(created.title);
      setBoardId(created.id);
      localStorage.setItem(LS_CURRENT_ID, String(created.id));
      setSaveStatus('saved');
      setLastSavedAt(new Date().toISOString());
      bootedRef.current = true;
      return {
        id: created.id, title: created.title, card_count: 0,
        created_at: null, updated_at: null,
      };
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, refreshBoardsList]);

  // ── Boot: fetch boards list. If none, migrate legacy state or seed. ─
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await refreshBoardsList();
        if (cancelled) return;
        if (list.length === 0) {
          // First-time user. Migrate any legacy localStorage state into a
          // real saved board so they don't lose their work.
          const legacy = readLegacyState();
          if (legacy) {
            await createBoard('My first board', legacy);
            try { localStorage.removeItem(LS_LEGACY_KEY); } catch { /* ignore */ }
          } else {
            await createBoard('Untitled board');
          }
        } else {
          // Open last-used board if it's still in the list; else newest.
          const lastIdRaw = localStorage.getItem(LS_CURRENT_ID);
          const lastId = lastIdRaw ? parseInt(lastIdRaw, 10) : NaN;
          const target = list.find(b => b.id === lastId) || list[0];
          await openBoard(target.id);
        }
      } catch (e: any) {
        if (!cancelled) setBootError('Could not load boards. Try reloading.');
      }
    })();
    return () => { cancelled = true; };
  }, [refreshBoardsList, openBoard, createBoard]);

  // ── Auto-save: debounced PUT to /boards/{id} on every state change ──
  useEffect(() => {
    if (!bootedRef.current || boardId == null) return;
    const snapshot = JSON.stringify(state);
    if (snapshot === lastSavedSnapshot.current) return;
    writeCachedState(boardId, state);  // optimistic local cache
    setSaveStatus('saving');
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      axios.put(`${API}/boards/${boardId}`, { state }, authHeader)
        .then((r) => {
          lastSavedSnapshot.current = snapshot;
          setSaveStatus('saved');
          setLastSavedAt(r.data?.updated_at || new Date().toISOString());
        })
        .catch(() => setSaveStatus('error'));
    }, 1200);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, boardId]);

  // Flush save before page unload so a quick edit + close doesn't lose data.
  useEffect(() => {
    const onBeforeUnload = () => {
      if (!bootedRef.current || boardId == null) return;
      const snapshot = JSON.stringify(state);
      if (snapshot === lastSavedSnapshot.current) return;
      // sendBeacon is the only thing browsers reliably ship during unload.
      try {
        const blob = new Blob(
          [JSON.stringify({ state, _t: token })],
          { type: 'application/json' },
        );
        // We don't have a beacon endpoint; fall back to sync XHR.
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', `${API}/boards/${boardId}`, false);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(JSON.stringify({ state }));
        // Mark unused so lint stops complaining
        void blob;
      } catch { /* ignore */ }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [state, boardId, token]);

  // Refresh the relative timestamp once a minute so the indicator stays honest.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const i = window.setInterval(() => setNowTick(n => n + 1), 30_000);
    return () => window.clearInterval(i);
  }, []);

  // ── Board actions ───────────────────────────────────────────────────
  const renameCurrentBoard = async (next: string) => {
    if (boardId == null) return;
    const trimmed = (next || '').trim();
    if (!trimmed || trimmed === boardTitle) {
      setRenamingTitle(null);
      return;
    }
    setBoardTitle(trimmed);
    setRenamingTitle(null);
    try {
      await axios.patch(`${API}/boards/${boardId}/title`,
        { title: trimmed }, authHeader);
      await refreshBoardsList();
    } catch { /* tolerate — title still shows in UI */ }
  };

  const switchToBoard = async (id: number) => {
    if (id === boardId) { setSwitcherOpen(false); return; }
    setSwitcherOpen(false);
    setSelected(null);
    // Flush pending save before navigating away.
    if (saveTimer.current) {
      window.clearTimeout(saveTimer.current);
      if (boardId != null) {
        try {
          await axios.put(`${API}/boards/${boardId}`, { state }, authHeader);
        } catch { /* ignore */ }
      }
    }
    await openBoard(id);
  };

  const handleNewBoard = async () => {
    setSwitcherOpen(false);
    setSelected(null);
    if (saveTimer.current && boardId != null) {
      window.clearTimeout(saveTimer.current);
      try { await axios.put(`${API}/boards/${boardId}`, { state }, authHeader); } catch { /* ignore */ }
    }
    const proposed = window.prompt('Name this board:', 'Untitled board') || 'Untitled board';
    await createBoard(proposed);
  };

  const handleDeleteBoard = async (id: number) => {
    const tgt = boardsList.find(b => b.id === id);
    const label = tgt ? `"${tgt.title}"` : 'this board';
    if (!window.confirm(`Delete ${label}? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/boards/${id}`, authHeader);
      try { localStorage.removeItem(LS_CACHE_PREFIX + id); } catch { /* ignore */ }
      const list = await refreshBoardsList();
      if (id === boardId) {
        if (list.length > 0) {
          await openBoard(list[0].id);
        } else {
          await createBoard('Untitled board');
        }
      }
    } catch { /* ignore */ }
  };

  const theme = state.theme;
  const bg     = theme === 'light' ? '#FAFAF9' : '#0F0F12';
  const surface = theme === 'light' ? '#FFFFFF' : '#1A1A20';
  const border  = theme === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)';
  const fg     = theme === 'light' ? '#1A1A1A' : '#F0F0F0';
  const fgMute = theme === 'light' ? '#6B7280' : '#9CA3AF';
  const accent = '#C9A96E';
  const grid   = theme === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.04)';
  const shadow = theme === 'light'
    ? '0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.08)'
    : '0 1px 3px rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.5)';

  // ─── Viewport: pan + zoom ─────────────────────────────────────────────

  const [panning, setPanning] = useState(false);
  const panStart = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const canvasToScreen = (x: number, y: number) => {
    const { tx, ty, scale } = state.viewport;
    return { x: x * scale + tx, y: y * scale + ty };
  };
  const screenToCanvas = (x: number, y: number) => {
    const { tx, ty, scale } = state.viewport;
    return { x: (x - tx) / scale, y: (y - ty) / scale };
  };

  const onBgMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).dataset.role !== 'canvas-bg') return;
    // Prevent browser from starting a drag-selection over the whole page
    e.preventDefault();
    if (tool === 'select') {
      setPanning(true);
      panStart.current = {
        x: e.clientX, y: e.clientY,
        tx: state.viewport.tx, ty: state.viewport.ty,
      };
      setSelected(null);
    } else {
      // Drop a new card at click position
      const { x, y } = screenToCanvas(e.clientX, e.clientY);
      const id = genId();
      let card: BoardCard;
      if (tool === 'sticky') {
        card = {
          id, type: 'sticky', x: x - 100, y: y - 70, w: 200, h: 140,
          color: pendingColor, text: '',
        };
      } else if (tool === 'text') {
        card = {
          id, type: 'text', x: x - 100, y: y - 20, w: 200, h: 50, text: '',
        };
      } else { // link
        const href = window.prompt('Paste a URL:');
        if (!href) { setTool('select'); return; }
        const title = window.prompt('Label (optional):') || href;
        card = {
          id, type: 'link', x: x - 100, y: y - 30, w: 220, h: 60,
          href, title,
        };
      }
      setState(s => ({ ...s, cards: [...s.cards, card] }));
      setSelected(id);
      // Keep tool active so the user can drop multiple in a row. ESC or
      // clicking the Select tool exits drop-mode.
      if (tool === 'link') setTool('select'); // link prompt is one-shot
    }
  };

  // ESC clears the active tool (back to select)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTool('select');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* ── Sit-in meeting live pickup ────────────────────────────────────
   *  The mount-time sweep of coast.meeting.pending.* localStorage keys
   *  now happens INSIDE openBoard (via mergePendingMeetings), so the
   *  merge lands after the backend state arrives and isn't subsequently
   *  overwritten by the fetch. This effect handles only the OTHER case:
   *  saves dispatched while Board is already mounted. The SitInFab
   *  fires a `coast:meeting-saved` CustomEvent for those.
   *  Cards land in a small cascade near (200, 200) in board space so
   *  they're always visible regardless of current viewport pan.
   */
  useEffect(() => {
    type PendingMeeting = NonNullable<BoardCard['meeting']>;
    const onSaved = (ev: Event) => {
      const e = ev as CustomEvent<PendingMeeting>;
      if (!e.detail) return;
      setState(s => {
        const card: BoardCard = {
          id:    genId(),
          type:  'meeting',
          x:     200 + (s.cards.length % 6) * 24,
          y:     200 + (s.cards.length % 6) * 24,
          w:     440, h: 320,
          meeting: e.detail,
        };
        return { ...s, cards: [...s.cards, card] };
      });
      // The SitInFab also writes to localStorage; both paths converge,
      // so once we've added the card here, remove any matching pending
      // entry to keep the openBoard merge from also adding a duplicate
      // on the user's next navigation.
      try {
        const keys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith('coast.meeting.pending.')) keys.push(k);
        }
        for (const k of keys) localStorage.removeItem(k);
      } catch { /* ignore */ }
    };
    window.addEventListener('coast:meeting-saved', onSaved as EventListener);
    return () => window.removeEventListener('coast:meeting-saved', onSaved as EventListener);
  }, []);

  /* ── Focus highlight from RecentMeetingPill click ────────────────────
   *  When the URL contains ?focus=<marker-id> (set by the pill that
   *  appears on non-Board pages after a meeting is saved), find the
   *  newest meeting card on the board, scroll the viewport so it sits
   *  centre-screen, and add it to flashCardId for ~3 seconds for a
   *  visual confirmation. After consuming we strip the param so a
   *  refresh isn't a re-focus.
   *
   *  Runs only after the board has booted (bootedRef.current === true)
   *  AND after openBoard's merge has already added any pending meeting
   *  cards — i.e. on every state.cards change, we re-check the URL.
   */
  useEffect(() => {
    if (!bootedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const focus = params.get('focus');
    if (!focus) return;
    // Find the newest meeting card by recorded_at descending.
    const meetingCards = state.cards
      .filter(c => c.type === 'meeting' && c.meeting?.recorded_at)
      .slice()
      .sort((a, b) => {
        const ar = a.meeting?.recorded_at || '';
        const br = b.meeting?.recorded_at || '';
        return br.localeCompare(ar);
      });
    if (meetingCards.length === 0) return;
    const target = meetingCards[0];

    // Centre the viewport on the target card. Card position is in
    // board-space (target.x, target.y); viewport.tx/ty/scale convert
    // to screen-space. To put (cx, cy) at screen centre we set
    //   tx = screen.cx - cx * scale
    //   ty = screen.cy - cy * scale
    // with cx, cy = card centre in board space.
    setState(s => {
      const cx = target.x + target.w / 2;
      const cy = target.y + target.h / 2;
      const scale = s.viewport.scale;
      return {
        ...s,
        viewport: {
          scale,
          tx: window.innerWidth / 2  - cx * scale,
          ty: window.innerHeight / 2 - cy * scale,
        },
      };
    });
    setFlashCardId(target.id);
    setSelected(target.id);
    // Strip ?focus so a refresh / back-button doesn't re-fire.
    const cleanUrl = window.location.pathname + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
    const t = window.setTimeout(() => setFlashCardId(null), 3200);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.cards]);

  useEffect(() => {
    if (!panning) return;
    const onMove = (e: MouseEvent) => {
      const ps = panStart.current;
      if (!ps) return;
      setState(s => ({
        ...s,
        viewport: {
          ...s.viewport,
          tx: ps.tx + (e.clientX - ps.x),
          ty: ps.ty + (e.clientY - ps.y),
        },
      }));
    };
    const onUp = () => { setPanning(false); panStart.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [panning]);

  // Zoom — Cmd/Ctrl + wheel
  const onWheel = (e: React.WheelEvent) => {
    if (!(e.metaKey || e.ctrlKey)) return;
    e.preventDefault();
    const delta = -e.deltaY * 0.003;
    setState(s => {
      const oldScale = s.viewport.scale;
      const newScale = Math.max(0.25, Math.min(3, oldScale * (1 + delta)));
      // Zoom around cursor: keep cursor's canvas-point fixed
      const cx = e.clientX, cy = e.clientY;
      const canvasX = (cx - s.viewport.tx) / oldScale;
      const canvasY = (cy - s.viewport.ty) / oldScale;
      return {
        ...s,
        viewport: {
          tx: cx - canvasX * newScale,
          ty: cy - canvasY * newScale,
          scale: newScale,
        },
      };
    });
  };

  // ─── Card drag + resize ───────────────────────────────────────────────

  const cardDrag = useRef<{
    id: string;
    startX: number; startY: number;
    cardX:  number; cardY:  number;
    cardW:  number; cardH:  number;
    mode:   'move' | 'resize';
  } | null>(null);

  const startCardDrag = (e: React.MouseEvent, card: BoardCard,
                          mode: 'move' | 'resize') => {
    e.stopPropagation();
    e.preventDefault();  // stop browser text-selection on drag
    setSelected(card.id);
    cardDrag.current = {
      id: card.id, startX: e.clientX, startY: e.clientY,
      cardX: card.x, cardY: card.y, cardW: card.w, cardH: card.h,
      mode,
    };
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const c = cardDrag.current;
      if (!c) return;
      const dx = (e.clientX - c.startX) / state.viewport.scale;
      const dy = (e.clientY - c.startY) / state.viewport.scale;
      setState(s => ({
        ...s,
        cards: s.cards.map(card => {
          if (card.id !== c.id) return card;
          if (c.mode === 'move') {
            return { ...card, x: c.cardX + dx, y: c.cardY + dy };
          }
          return {
            ...card,
            w: Math.max(120, c.cardW + dx),
            h: Math.max(60, c.cardH + dy),
          };
        }),
      }));
    };
    const onUp = () => { cardDrag.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [state.viewport.scale]);

  // ─── Card edit + delete ───────────────────────────────────────────────

  const updateCard = (id: string, patch: Partial<BoardCard>) => {
    setState(s => ({
      ...s,
      cards: s.cards.map(c => c.id === id ? { ...c, ...patch } : c),
    }));
  };
  const deleteCard = (id: string) => {
    setState(s => ({ ...s, cards: s.cards.filter(c => c.id !== id) }));
    if (selected === id) setSelected(null);
  };

  // Keyboard: delete to remove selected card
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const tgt = e.target as HTMLElement;
        if (tgt.tagName === 'TEXTAREA' || tgt.tagName === 'INPUT') return;
        if (selected) {
          e.preventDefault();
          deleteCard(selected);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  // ─── Render ───────────────────────────────────────────────────────────

  const stickyColorObj = STICKY_COLORS.find(c => c.id === pendingColor) || STICKY_COLORS[0];

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: bg, color: fg,
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Inter", system-ui, sans-serif',
        fontWeight: 300, overflow: 'hidden',
        // Globally disable browser text-selection on the board so click-and-drag
        // doesn't paint the whole page blue. Inputs/textareas re-enable it
        // explicitly via the CSS rule below.
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}>
      <style>{`
        @keyframes coastFadeIn {
          from { opacity: 0; transform: translateY(6px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes coastPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        button:focus, select:focus, textarea:focus, input:focus { outline: none; }
        /* Selection is OFF globally on board (above). Turn it back ON only
           inside editable text — sticky / text / link / inputs. */
        textarea, input, [data-editable="true"] {
          user-select: text;
          -webkit-user-select: text;
        }
        .coast-board-card:hover { box-shadow: 0 2px 4px rgba(0,0,0,0.05), 0 12px 32px rgba(0,0,0,0.12); }
      `}</style>

      {/* ─── Canvas ───────────────────────────────────────────────── */}
      <div
        ref={canvasRef}
        data-role="canvas-bg"
        onMouseDown={onBgMouseDown}
        onWheel={onWheel}
        style={{
          position: 'absolute', inset: 0,
          cursor: panning ? 'grabbing' : tool === 'select' ? 'grab' : 'crosshair',
          backgroundImage: `radial-gradient(circle, ${grid} 1px, transparent 1px)`,
          backgroundSize: `${24 * state.viewport.scale}px ${24 * state.viewport.scale}px`,
          backgroundPosition: `${state.viewport.tx}px ${state.viewport.ty}px`,
        }}>

        {/* Cards layer */}
        <div style={{
          position: 'absolute', inset: 0,
          transform: `translate(${state.viewport.tx}px, ${state.viewport.ty}px) scale(${state.viewport.scale})`,
          transformOrigin: '0 0',
          pointerEvents: 'none',  // cards re-enable
        }}>
          {state.cards.map(card => renderCard(card))}
        </div>
      </div>

      {/* ─── Top bar ──────────────────────────────────────────────── */}
      <div style={{
        position: 'absolute', top: 16, left: 16, right: 16,
        display: 'flex', gap: 12, alignItems: 'center',
        pointerEvents: 'none',
      }}>
        <div style={{
          background: surface, border: `1px solid ${border}`,
          borderRadius: 14, padding: '8px 14px',
          boxShadow: shadow, pointerEvents: 'auto',
          display: 'flex', alignItems: 'center', gap: 12,
          position: 'relative',
        }}>
          <div style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: 18, fontWeight: 700, color: accent,
            letterSpacing: '0.02em',
          }}>
            COAST
          </div>
          <div style={{ width: 1, height: 20, background: border }} />

          {/* Editable board title */}
          {renamingTitle !== null ? (
            <input
              autoFocus
              value={renamingTitle}
              onChange={(e) => setRenamingTitle(e.target.value)}
              onBlur={() => renameCurrentBoard(renamingTitle)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') renameCurrentBoard(renamingTitle);
                else if (e.key === 'Escape') setRenamingTitle(null);
              }}
              style={{
                background: 'transparent', border: 'none', outline: 'none',
                color: fg, fontSize: 14, fontWeight: 500,
                fontFamily: 'inherit', minWidth: 180,
                borderBottom: `1px solid ${accent}`, padding: '2px 0',
              }}
            />
          ) : (
            <button
              onClick={() => boardId != null && setRenamingTitle(boardTitle)}
              title="Click to rename"
              disabled={boardId == null}
              style={{
                background: 'transparent', border: 'none', cursor: boardId == null ? 'default' : 'text',
                color: fg, fontSize: 14, fontWeight: 500, padding: '2px 0',
                fontFamily: 'inherit', maxWidth: 280, overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
              {boardId == null ? (bootError || 'Loading…') : boardTitle}
            </button>
          )}

          {/* Switcher trigger */}
          <button
            onClick={() => { refreshBoardsList(); setSwitcherOpen(o => !o); }}
            title="Switch board"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: fgMute, padding: 2, display: 'flex', alignItems: 'center',
            }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {/* Save indicator */}
          <div style={{
            color: saveStatus === 'error' ? '#CC2200'
                 : saveStatus === 'saving' ? fgMute
                 : fgMute,
            fontSize: 10, fontWeight: 400,
            letterSpacing: '0.04em', textTransform: 'uppercase',
            marginLeft: 4,
          }}>
            {boardId == null ? '' :
             saveStatus === 'saving' ? 'Saving…' :
             saveStatus === 'error'  ? 'Save failed' :
             lastSavedAt ? `Saved ${fmtRelative(lastSavedAt)}` :
             'Saved'}
          </div>

          {/* Switcher dropdown */}
          {switcherOpen && (
            <>
              <div
                onClick={() => setSwitcherOpen(false)}
                style={{
                  position: 'fixed', inset: 0, zIndex: 40,
                  background: 'transparent',
                }} />
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', left: 0,
                background: surface, border: `1px solid ${border}`,
                borderRadius: 12, boxShadow: shadow,
                minWidth: 320, maxWidth: 420, maxHeight: 420,
                overflowY: 'auto', zIndex: 41,
                padding: 6,
              }}>
                <div style={{
                  fontSize: 10, color: fgMute, fontWeight: 500,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  padding: '8px 10px 4px',
                }}>
                  Your boards ({boardsList.length})
                </div>
                {boardsList.map(b => {
                  const isActive = b.id === boardId;
                  return (
                    <div key={b.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 10px', borderRadius: 8,
                        background: isActive ? (theme === 'dark' ? 'rgba(201,169,110,0.12)' : 'rgba(201,169,110,0.10)') : 'transparent',
                        cursor: isActive ? 'default' : 'pointer',
                      }}
                      onClick={() => !isActive && switchToBoard(b.id)}
                      onMouseEnter={(e) => {
                        if (!isActive) (e.currentTarget as HTMLDivElement).style.background = theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                      }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          color: fg, fontSize: 13, fontWeight: 500,
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {b.title}
                        </div>
                        <div style={{ color: fgMute, fontSize: 10, marginTop: 2 }}>
                          {b.card_count} card{b.card_count === 1 ? '' : 's'}
                          {b.updated_at ? ` · edited ${fmtRelative(b.updated_at)}` : ''}
                        </div>
                      </div>
                      {isActive && (
                        <div style={{
                          fontSize: 9, fontWeight: 600, color: accent,
                          letterSpacing: '0.08em',
                        }}>OPEN</div>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteBoard(b.id); }}
                        title="Delete board"
                        style={{
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          color: fgMute, padding: 4, opacity: 0.6,
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; (e.currentTarget as HTMLButtonElement).style.color = '#CC2200'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.6'; (e.currentTarget as HTMLButtonElement).style.color = fgMute; }}>
                        {ICON_TRASH}
                      </button>
                    </div>
                  );
                })}
                <div style={{ height: 1, background: border, margin: '6px 0' }} />
                <button
                  onClick={handleNewBoard}
                  style={{
                    width: '100%', textAlign: 'left',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    padding: '8px 10px', borderRadius: 8,
                    color: accent, fontSize: 13, fontWeight: 500,
                    fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = theme === 'dark' ? 'rgba(201,169,110,0.10)' : 'rgba(201,169,110,0.08)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  New board
                </button>
              </div>
            </>
          )}
        </div>
        <span style={{ flex: 1 }} />
        <button
          onClick={() => setVoiceOpen(true)}
          title="Record a voice memo — AI parses it into structured property data"
          style={{
            ...iconBtnStyle(surface, border, fg, shadow),
            background: accent, color: '#fff', border: `1px solid ${accent}`,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="2" width="6" height="13" rx="3" />
            <path d="M5 11a7 7 0 0014 0M12 18v3" />
          </svg>
          Record
        </button>
        <button
          onClick={() => setCombineOpen(true)}
          title="Combine multiple captures on this board into one stitched deck"
          disabled={deckableCardsFromBoard(state.cards as any).length < 2}
          style={{
            ...iconBtnStyle(surface, border, fg, shadow),
            display: 'flex', alignItems: 'center', gap: 6,
            opacity: deckableCardsFromBoard(state.cards as any).length < 2 ? 0.45 : 1,
            cursor: deckableCardsFromBoard(state.cards as any).length < 2 ? 'not-allowed' : 'pointer',
          }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="13" height="11" rx="1" />
            <rect x="8" y="9" width="13" height="11" rx="1" />
          </svg>
          Combine deck
        </button>
        <button
          onClick={() => setState(s => ({
            ...s, viewport: { tx: 0, ty: 0, scale: 1 },
          }))}
          style={iconBtnStyle(surface, border, fg, shadow)}>
          Reset view
        </button>
        <button
          onClick={() => setState(s => ({
            ...s, theme: s.theme === 'light' ? 'dark' : 'light',
          }))}
          style={iconBtnStyle(surface, border, fg, shadow)}>
          {theme === 'light' ? 'Dark' : 'Light'}
        </button>
        {onOpenMap && (
          <button
            onClick={onOpenMap}
            title="Open the urban-intelligence Map view"
            style={iconBtnStyle(surface, border, fg, shadow)}>
            Map
          </button>
        )}
        <button
          onClick={onExitToDashboard}
          style={iconBtnStyle(surface, border, fg, shadow)}>
          Dashboard →
        </button>
      </div>

      {/* ─── Active-tool hint (centered, only when not in Select) ── */}
      {tool !== 'select' && (
        <div style={{
          position: 'absolute', top: 70, left: '50%',
          transform: 'translateX(-50%)',
          background: surface, border: `1px solid ${border}`,
          borderRadius: 100, padding: '6px 16px',
          boxShadow: shadow,
          fontSize: 11, color: fg, fontWeight: 500,
          letterSpacing: '0.04em',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{
            color: accent, fontWeight: 700,
            textTransform: 'uppercase', fontSize: 10,
            letterSpacing: '0.12em',
          }}>
            {tool === 'sticky' ? 'Sticky' :
             tool === 'text'   ? 'Text'   :
             tool === 'link'   ? 'Link'   : tool}
          </span>
          <span>·</span>
          <span style={{ color: fgMute }}>
            Click anywhere on the board to drop
          </span>
          <span>·</span>
          <span style={{ color: fgMute }}>ESC to cancel</span>
        </div>
      )}

      {/* ─── Floating toolbar (left) ──────────────────────────────── */}
      <div style={{
        position: 'absolute', left: 16, top: '50%',
        transform: 'translateY(-50%)',
        background: surface, border: `1px solid ${border}`,
        borderRadius: 18, padding: 10,
        boxShadow: shadow,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        {[
          { id: 'select' as const, label: 'Select (V) — drag to pan',  icon: ICON_SELECT },
          { id: 'sticky' as const, label: 'Sticky note (S)',           icon: ICON_STICKY },
          { id: 'text'   as const, label: 'Text box (T)',              icon: ICON_TEXT },
          { id: 'link'   as const, label: 'Link (L)',                  icon: ICON_LINK },
        ].map(t => (
          <button key={t.id} onClick={() => {
            if (t.id === 'sticky') setPaletteOpen(o => !o);
            else setPaletteOpen(false);
            setTool(t.id);
          }}
            title={t.label}
            style={toolBtnStyle(
              tool === t.id, surface, border, fg, accent, theme,
            )}>
            {t.icon}
          </button>
        ))}
        <div style={{ height: 1, background: border, margin: '4px 0' }} />
        <button
          onClick={() => setAddSectionOpen(true)}
          title="Drop a Coast section card"
          style={toolBtnStyle(false, surface, border, fg, accent, theme)}>
          {ICON_SECTION}
        </button>
      </div>

      {/* Sticky color palette (slides out from toolbar) */}
      {paletteOpen && (
        <div style={{
          position: 'absolute', left: 72, top: '50%',
          transform: 'translateY(-50%)',
          background: surface, border: `1px solid ${border}`,
          borderRadius: 14, padding: 8, boxShadow: shadow,
          display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          {STICKY_COLORS.map(c => (
            <button key={c.id}
              onClick={() => { setPendingColor(c.id); setPaletteOpen(false); }}
              title={c.id}
              style={{
                width: 28, height: 28, borderRadius: 8,
                background: theme === 'light' ? c.light : c.dark,
                border: pendingColor === c.id
                  ? `2px solid ${accent}` : `1px solid ${border}`,
                cursor: 'pointer', padding: 0,
              }} />
          ))}
        </div>
      )}

      {/* Unified Add overlay — live data, charts, sections, media */}
      {addSectionOpen && (
        <div onClick={() => setAddSectionOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 30,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, backdropFilter: 'blur(6px)',
          }}>
          <div onClick={e => e.stopPropagation()}
            style={{
              background: surface, color: fg,
              border: `1px solid ${border}`,
              borderRadius: 20, padding: 26, boxShadow: shadow,
              minWidth: 540, maxWidth: 760,
              maxHeight: '85vh', overflowY: 'auto',
            }}>
            <div style={{
              fontFamily: 'Fraunces, Georgia, serif', fontSize: 20, fontWeight: 700,
              marginBottom: 4, color: fg,
            }}>
              Add to board
            </div>
            <div style={{
              fontSize: 13, color: fgMute, marginBottom: 22, fontWeight: 400,
            }}>
              Drop live Coast data, charts, sections, or media anywhere on the canvas.
            </div>

            {/* LIVE DATA */}
            <SectionHeading text="Live data" color={accent} fg={fg} />
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 22,
            }}>
              <PickerCard fg={fg} fgMute={fgMute} border={border}
                title="Market snapshot"
                desc="One MSA, 6 macro indicators with YoY"
                onClick={() => addCardAtCenter({
                  type: 'live', liveKind: 'market-snapshot',
                  w: 360, h: 220,
                }, setState, state, setAddSectionOpen, setSelected)}
              />
              <PickerCard fg={fg} fgMute={fgMute} border={border}
                title="Peer ranking"
                desc="Top-6 buildings for one persona"
                onClick={() => addCardAtCenter({
                  type: 'live', liveKind: 'peer-rank',
                  sheetId: 2, personaId: 'value',
                  w: 340, h: 240,
                }, setState, state, setAddSectionOpen, setSelected)}
              />
              <PickerCard fg={fg} fgMute={fgMute} border={border}
                title="Property card"
                desc="One building's niche + key stats"
                onClick={() => addCardAtCenter({
                  type: 'live', liveKind: 'property-card',
                  sheetId: 2,
                  w: 280, h: 220,
                }, setState, state, setAddSectionOpen, setSelected)}
              />
            </div>

            {/* CHARTS */}
            <SectionHeading text="Charts" color={accent} fg={fg} />
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 22,
            }}>
              <PickerCard fg={fg} fgMute={fgMute} border={border}
                title="Rent comparison"
                desc="Bar chart — cheapest building first"
                onClick={() => addCardAtCenter({
                  type: 'chart', chartKind: 'rent-bar',
                  chartConfig: { sheetId: 2 },
                  w: 380, h: 320,
                }, setState, state, setAddSectionOpen, setSelected)}
              />
              <PickerCard fg={fg} fgMute={fgMute} border={border}
                title="Amenity comparison"
                desc="Bar chart — most amenities first"
                onClick={() => addCardAtCenter({
                  type: 'chart', chartKind: 'amenity-bar',
                  chartConfig: { sheetId: 2 },
                  w: 380, h: 320,
                }, setState, state, setAddSectionOpen, setSelected)}
              />
              <PickerCard fg={fg} fgMute={fgMute} border={border}
                title="Rent vs amenities"
                desc="Scatter plot — find the sweet spot"
                onClick={() => addCardAtCenter({
                  type: 'chart', chartKind: 'rent-vs-sqft',
                  chartConfig: { sheetId: 2 },
                  w: 360, h: 280,
                }, setState, state, setAddSectionOpen, setSelected)}
              />
              <PickerCard fg={fg} fgMute={fgMute} border={border}
                title="Newest construction"
                desc="Bar chart — newest builds first"
                onClick={() => addCardAtCenter({
                  type: 'chart', chartKind: 'persona-rank',
                  chartConfig: { sheetId: 2 },
                  w: 380, h: 320,
                }, setState, state, setAddSectionOpen, setSelected)}
              />
            </div>

            {/* MEDIA */}
            <SectionHeading text="Media" color={accent} fg={fg} />
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 22,
            }}>
              <PickerCard fg={fg} fgMute={fgMute} border={border}
                title="Image (upload)"
                desc="Pick a file from your computer"
                onClick={() => {
                  pickLocalFile('image/*', 4, (dataUrl) => {
                    addCardAtCenter({
                      type: 'image', href: dataUrl,
                      w: 320, h: 240,
                    }, setState, state, setAddSectionOpen, setSelected);
                  }, msg => alert(msg));
                }}
              />
              <PickerCard fg={fg} fgMute={fgMute} border={border}
                title="Image (URL)"
                desc="Paste a URL to a hosted image"
                onClick={() => {
                  const url = window.prompt('Image URL:');
                  if (!url) return;
                  addCardAtCenter({
                    type: 'image', href: url,
                    w: 280, h: 200,
                  }, setState, state, setAddSectionOpen, setSelected);
                }}
              />
              <PickerCard fg={fg} fgMute={fgMute} border={border}
                title="PDF (upload)"
                desc="Pick a PDF (≤4MB) from your computer"
                onClick={() => {
                  pickLocalFile('application/pdf', 4, (dataUrl, name) => {
                    addCardAtCenter({
                      type: 'pdf', href: dataUrl, title: name || 'PDF',
                      w: 360, h: 460,
                    }, setState, state, setAddSectionOpen, setSelected);
                  }, msg => alert(msg));
                }}
              />
              <PickerCard fg={fg} fgMute={fgMute} border={border}
                title="PDF (URL)"
                desc="Embed by URL (must allow framing)"
                onClick={() => {
                  const url = window.prompt('PDF URL:');
                  if (!url) return;
                  addCardAtCenter({
                    type: 'pdf', href: url, title: 'PDF',
                    w: 360, h: 460,
                  }, setState, state, setAddSectionOpen, setSelected);
                }}
              />
            </div>

            {/* SECTION LINKS */}
            <SectionHeading text="Coast section links" color={accent} fg={fg} />
            <div style={{
              fontSize: 11, color: fgMute, fontWeight: 400,
              marginBottom: 10, lineHeight: 1.45,
            }}>
              Drop a card that double-click opens the section in the dashboard.
            </div>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10,
            }}>
              {SECTIONS.map(s => (
                <PickerCard key={s.id} fg={fg} fgMute={fgMute} border={border}
                  title={s.label}
                  desc={s.desc}
                  onClick={() => addCardAtCenter({
                    type: 'section', section: s.id, title: s.label,
                    w: 240, h: 140,
                  }, setState, state, setAddSectionOpen, setSelected)}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ─── Voice recorder overlay ──────────────────────────────── */}
      {voiceOpen && (
        <VoiceRecorderOverlay
          token={token}
          theme={theme}
          existingTables={state.cards
            .filter(c => c.type === 'voice-table' && c.voiceTable)
            .map(c => ({ id: c.id, title: c.voiceTable!.title, count: c.voiceTable!.rows.length }))}
          onCancel={() => setVoiceOpen(false)}
          onComplete={(payload, destination) => {
            setVoiceOpen(false);
            const newRow: VoiceMemoRow = {
              id: genId(),
              recorded_at: new Date().toISOString(),
              transcript:  payload.transcript,
              extracted:   payload.extracted,
              entities:    payload.entities,
              parse_error: payload.parse_error,
            };
            if (destination.kind === 'append') {
              // Add to an existing voice-table
              setState(prev => ({
                ...prev,
                cards: prev.cards.map(c =>
                  c.id === destination.tableId && c.voiceTable
                    ? { ...c, voiceTable: { ...c.voiceTable,
                        rows: [...c.voiceTable.rows, newRow] } }
                    : c),
              }));
              setSelected(destination.tableId);
            } else if (destination.kind === 'new-table') {
              const c: BoardCard = {
                id: genId(),
                type: 'voice-table',
                x: 100 - state.viewport.tx / state.viewport.scale,
                y: 120 - state.viewport.ty / state.viewport.scale,
                w: 540, h: 360,
                voiceTable: {
                  title: destination.title || 'Property memos',
                  rows:  [newRow],
                },
              };
              setState(prev => ({ ...prev, cards: [...prev.cards, c] }));
              setSelected(c.id);
            } else {
              // 'new-card' — single voice memo
              const c: BoardCard = {
                id: genId(),
                type: 'voice',
                x: 100 - state.viewport.tx / state.viewport.scale,
                y: 120 - state.viewport.ty / state.viewport.scale,
                w: 360, h: 320,
                voice: { ...newRow },
              };
              setState(prev => ({ ...prev, cards: [...prev.cards, c] }));
              setSelected(c.id);
            }
          }}
        />
      )}

      {/* ─── Combine-deck picker + the actual viewer when sources are picked ── */}
      {combineOpen && (
        <CombineDeckModal
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          cards={deckableCardsFromBoard(state.cards as any)}
          onClose={() => setCombineOpen(false)}
          onConfirm={(picks) => {
            setCombineOpen(false);
            setCombinedSources(picks);
          }}
        />
      )}
      {combinedSources && (
        <DeckViewer
          combined={combinedSources}
          pdfTitle={`Combined brief · ${combinedSources.length} sources`}
          token={token}
          onClose={() => setCombinedSources(null)}
        />
      )}

      {/* ─── Selection handles (screen-space, always 22px visual) ── */}
      {(() => {
        if (!selected) return null;
        const card = state.cards.find(c => c.id === selected);
        if (!card) return null;
        const tl = canvasToScreen(card.x, card.y);
        const br = canvasToScreen(card.x + card.w, card.y + card.h);
        return (
          <>
            {/* Selection outline (1.5px gold ring) */}
            <div style={{
              position: 'absolute',
              left: tl.x - 2, top: tl.y - 2,
              width:  br.x - tl.x + 4,
              height: br.y - tl.y + 4,
              border: `1.5px solid ${accent}`,
              borderRadius: 16,
              pointerEvents: 'none',
              boxShadow: theme === 'dark' ? `0 0 12px ${accent}88` : 'none',
            }} />
            {/* Resize handle (bottom-right) */}
            <div
              onMouseDown={(e) => startCardDrag(e, card, 'resize')}
              title="Drag to resize"
              style={{
                position: 'absolute',
                left: br.x - 9, top: br.y - 9,
                width: 18, height: 18, cursor: 'nwse-resize',
                background: accent, borderRadius: 5,
                boxShadow: shadow, pointerEvents: 'auto',
                border: `2px solid ${surface}`,
              }} />
            {/* Delete button (top-right of card) */}
            <button
              onMouseDown={e => e.stopPropagation()}
              onClick={() => deleteCard(card.id)}
              title="Delete (or press Delete)"
              style={{
                position: 'absolute',
                left: br.x - 12, top: tl.y - 12,
                width: 26, height: 26, borderRadius: 13,
                background: surface, border: `1px solid ${border}`,
                color: fg, cursor: 'pointer',
                boxShadow: shadow, pointerEvents: 'auto',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 0,
              }}>
              {ICON_TRASH}
            </button>
          </>
        );
      })()}

      {/* ─── Bottom-right zoom indicator ──────────────────────────── */}
      <div style={{
        position: 'absolute', right: 16, bottom: 16,
        background: surface, border: `1px solid ${border}`,
        borderRadius: 12, padding: '8px 14px',
        boxShadow: shadow,
        fontSize: 12, color: fgMute, fontWeight: 500,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {Math.round(state.viewport.scale * 100)}%   ·   {state.cards.length} cards
      </div>

      {/* ─── Help footer ──────────────────────────────────────────── */}
      <div style={{
        position: 'absolute', left: 16, bottom: 16,
        fontSize: 11, color: fgMute, fontWeight: 400,
        background: surface, border: `1px solid ${border}`,
        borderRadius: 12, padding: '8px 14px', boxShadow: shadow,
      }}>
        Drag empty space to pan · Cmd+scroll to zoom · Delete to remove a card
      </div>
    </div>
  );

  // ─── Card renderer ────────────────────────────────────────────────────

  function renderCard(card: BoardCard) {
    const isSelected = selected === card.id;
    const baseStyle: React.CSSProperties = {
      position: 'absolute',
      left: card.x, top: card.y,
      width: card.w, height: card.h,
      pointerEvents: 'auto',
      boxShadow: shadow,
      borderRadius: 14,
      transition: 'box-shadow 0.15s',
    };

    let body: React.ReactNode = null;
    if (card.type === 'sticky') {
      const c = STICKY_COLORS.find(x => x.id === card.color) || STICKY_COLORS[0];
      body = (
        <div
          onMouseDown={(e) => startCardDrag(e, card, 'move')}
          style={{
            ...baseStyle,
            background: theme === 'light' ? c.light : c.dark,
            color: theme === 'light' ? c.text : '#F5F5F5',
            borderRadius: 10,
            padding: 14,
            cursor: 'move',
            display: 'flex',
            border: `1px solid ${theme === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.1)'}`,
          }}>
          <textarea
            value={card.text || ''}
            onChange={e => updateCard(card.id, { text: e.target.value })}
            onMouseDown={e => {
              e.stopPropagation();
              setSelected(card.id);
            }}
            placeholder="Type here…"
            style={{
              flex: 1, background: 'transparent', border: 'none',
              outline: 'none', resize: 'none',
              fontFamily: 'inherit', fontWeight: 400, fontSize: 14,
              color: 'inherit', lineHeight: 1.4,
            }}
          />
        </div>
      );
    } else if (card.type === 'text') {
      body = (
        <div
          onMouseDown={(e) => startCardDrag(e, card, 'move')}
          style={{
            ...baseStyle,
            background: surface, color: fg,
            border: `1px solid ${border}`,
            padding: 12,
            cursor: 'move', display: 'flex',
          }}>
          <textarea
            value={card.text || ''}
            onChange={e => updateCard(card.id, { text: e.target.value })}
            onMouseDown={e => {
              e.stopPropagation();
              setSelected(card.id);
            }}
            placeholder="Text…"
            style={{
              flex: 1, background: 'transparent', border: 'none',
              outline: 'none', resize: 'none',
              fontFamily: 'inherit', fontWeight: 300, fontSize: 14,
              color: 'inherit', lineHeight: 1.5,
            }}
          />
        </div>
      );
    } else if (card.type === 'link') {
      body = (
        <div
          onMouseDown={(e) => startCardDrag(e, card, 'move')}
          onDoubleClick={() => {
            if (card.href) window.open(card.href, '_blank', 'noopener,noreferrer');
          }}
          title="Double-click to open"
          style={{
            ...baseStyle,
            background: surface, color: fg,
            border: `1px solid ${border}`,
            padding: '12px 16px',
            cursor: 'move',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
          }}>
          <div style={{
            color: accent, fontSize: 10, fontWeight: 600,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            marginBottom: 4,
          }}>
            Link
          </div>
          <div style={{
            fontSize: 14, fontWeight: 500,
            overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {card.title || card.href}
          </div>
          <div style={{
            fontSize: 10, color: fgMute, marginTop: 2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            Double-click to open
          </div>
        </div>
      );
    } else if (card.type === 'section') {
      const cardCtx: CardCtx = { token, theme, fg, fgMute, accent, border, surface };
      body = (
        <div
          onMouseDown={(e) => startCardDrag(e, card, 'move')}
          onDoubleClick={() => onExitToDashboard()}
          title="Double-click to open in dashboard"
          style={{
            ...baseStyle,
            background: surface, color: fg,
            border: `1.5px solid ${accent}`,
            cursor: 'move',
            display: 'flex', flexDirection: 'column',
          }}>
          <SectionInsightCard sectionId={card.section || ''} ctx={cardCtx} />
        </div>
      );
    } else if (card.type === 'voice') {
      const cardCtx: CardCtx = { token, theme, fg, fgMute, accent, border, surface };
      body = (
        <div
          onMouseDown={(e) => startCardDrag(e, card, 'move')}
          style={{
            ...baseStyle,
            background: surface,
            border: `1px solid ${border}`,
            borderRadius: 14, cursor: 'move',
            display: 'flex', flexDirection: 'column',
          }}>
          <VoiceMemoCard card={card} ctx={cardCtx}
            onUpdate={(patch) => updateCard(card.id, patch)} />
        </div>
      );
    } else if (card.type === 'voice-table') {
      const cardCtx: CardCtx = { token, theme, fg, fgMute, accent, border, surface };
      body = (
        <div
          onMouseDown={(e) => startCardDrag(e, card, 'move')}
          style={{
            ...baseStyle,
            background: surface,
            border: `1px solid ${border}`,
            borderRadius: 14, cursor: 'move',
            display: 'flex', flexDirection: 'column',
          }}>
          <VoiceTableCard card={card} ctx={cardCtx}
            onUpdate={(patch) => updateCard(card.id, patch)}
            onOpenRecorder={() => setVoiceOpen(true)} />
        </div>
      );
    } else if (card.type === 'meeting') {
      const cardCtx: CardCtx = { token, theme, fg, fgMute, accent, border, surface };
      const isFlash = flashCardId === card.id;
      body = (
        <div
          onMouseDown={(e) => startCardDrag(e, card, 'move')}
          style={{
            ...baseStyle,
            background: surface,
            // Flash highlight when arriving from the RecentMeetingPill —
            // gold ring + soft outer glow that fades out via CSS keyframes
            // after ~3s when flashCardId is cleared.
            border: isFlash ? `2px solid ${accent}` : `1px solid ${border}`,
            boxShadow: isFlash
              ? `0 0 0 4px ${accent}33, 0 8px 28px ${accent}55`
              : undefined,
            transition: 'box-shadow 0.4s, border-color 0.4s',
            borderRadius: 14, cursor: 'move',
            display: 'flex', flexDirection: 'column',
          }}>
          <MeetingCard card={card} ctx={cardCtx}
            onUpdate={(patch) => updateCard(card.id, patch)} />
        </div>
      );
    } else if (card.type === 'live' || card.type === 'chart') {
      // Live / chart cards — fetch real data inside the component
      const cardCtx: CardCtx = {
        token, theme, fg, fgMute, accent, border, surface,
      };
      let inner: React.ReactNode = null;
      if (card.type === 'live') {
        if (card.liveKind === 'market-snapshot') {
          inner = (
            <MarketSnapshotCard
              card={card} ctx={cardCtx}
              onPickMsa={(cbsa) => updateCard(card.id, { msaCbsa: cbsa })}
            />
          );
        } else if (card.liveKind === 'peer-rank') {
          inner = <PeerRankCard card={card} ctx={cardCtx} />;
        } else if (card.liveKind === 'property-card') {
          inner = <PropertyCard card={card} ctx={cardCtx} />;
        }
      } else {
        inner = <ChartCard card={card} ctx={cardCtx} />;
      }
      body = (
        <div
          onMouseDown={(e) => startCardDrag(e, card, 'move')}
          style={{
            ...baseStyle,
            background: surface, color: fg,
            border: `1px solid ${border}`,
            borderRadius: 14, cursor: 'move',
            display: 'flex', flexDirection: 'column',
          }}>
          {inner}
        </div>
      );
    } else if (card.type === 'image') {
      body = (
        <div
          onMouseDown={(e) => startCardDrag(e, card, 'move')}
          style={{
            ...baseStyle,
            background: surface, border: `1px solid ${border}`,
            borderRadius: 14, cursor: 'move', overflow: 'hidden',
          }}>
          {card.href ? (
            <img src={card.href} alt={card.title || 'image'}
              draggable={false}
              style={{
                width: '100%', height: '100%', objectFit: 'cover',
                display: 'block', pointerEvents: 'none',
              }} />
          ) : (
            <div style={{
              padding: 14, color: fgMute, fontSize: 12,
            }}>
              No image URL.
            </div>
          )}
        </div>
      );
    } else if (card.type === 'pdf') {
      body = (
        <div
          onMouseDown={(e) => startCardDrag(e, card, 'move')}
          style={{
            ...baseStyle,
            background: surface, border: `1px solid ${border}`,
            borderRadius: 14, cursor: 'move', overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
          }}>
          <div style={{
            padding: '8px 14px', borderBottom: `1px solid ${border}`,
            color: accent, fontSize: 9, fontWeight: 700,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>PDF</span>
            <span style={{ flex: 1 }} />
            {card.href && (
              <a href={card.href} target="_blank" rel="noopener noreferrer"
                onMouseDown={e => e.stopPropagation()}
                style={{
                  color: accent, fontSize: 9, fontWeight: 700,
                  letterSpacing: '0.1em',
                }}>
                Open ↗
              </a>
            )}
          </div>
          {card.href ? (
            <iframe src={card.href} title={card.title || 'pdf'}
              style={{
                flex: 1, width: '100%', border: 'none',
                pointerEvents: cardDrag.current ? 'none' : 'auto',
              }} />
          ) : (
            <div style={{ padding: 14, color: fgMute, fontSize: 12 }}>
              No PDF URL.
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={card.id} style={{
        pointerEvents: 'auto',
        animation: 'coastFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
      }}>
        {body}
      </div>
    );
  }
}

// ─── Style helpers ─────────────────────────────────────────────────────

function iconBtnStyle(surface: string, border: string, fg: string, shadow: string): React.CSSProperties {
  return {
    background: surface, border: `1px solid ${border}`,
    borderRadius: 12, padding: '8px 14px',
    color: fg, fontSize: 12, fontWeight: 500, cursor: 'pointer',
    pointerEvents: 'auto', boxShadow: shadow,
    fontFamily: 'inherit',
  };
}

function toolBtnStyle(active: boolean, surface: string, border: string,
                      fg: string, accent: string, theme: Theme): React.CSSProperties {
  return {
    width: 38, height: 38, borderRadius: 12,
    background: active
      ? accent
      : theme === 'light' ? '#F5F5F2' : '#252530',
    color: active ? '#FFFFFF' : fg,
    border: `1px solid ${active ? accent : border}`,
    cursor: 'pointer', padding: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.15s',
    fontFamily: 'inherit',
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  LIVE DATA CARDS — fetch real Coast data on mount, cache per-session
// ═══════════════════════════════════════════════════════════════════════

interface CardCtx {
  token:  string;
  theme:  Theme;
  fg:     string;
  fgMute: string;
  accent: string;
  border: string;
  surface: string;
}

// In-memory cache so dragging/resizing doesn't refetch
const _cache: Record<string, unknown> = {};

function useCachedFetch<T>(key: string, fetcher: () => Promise<T>):
  { data: T | null; loading: boolean; error: string | null; refresh: () => void } {
  const [data, setData] = useState<T | null>(key ? ((_cache[key] as T) || null) : null);
  const [loading, setLoading] = useState(!!key && !_cache[key]);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!key) { setLoading(false); return; }  // empty key = don't fetch
    let alive = true;
    if (_cache[key] && tick === 0) { setLoading(false); return; }
    setLoading(true);
    fetcher()
      .then(d => { if (!alive) return; _cache[key] = d; setData(d); setError(null); })
      .catch(e => {
        if (!alive) return;
        setError(e?.response?.data?.detail || e?.message || 'fetch failed');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick]);
  return { data, loading, error, refresh: () => { if (key) { delete _cache[key]; setTick(n => n + 1); } } };
}

// ── Market snapshot card: pick one MSA, show the 12 macro indicators ──

function MarketSnapshotCard({ card, ctx, onPickMsa }: {
  card: BoardCard;
  ctx: CardCtx;
  onPickMsa: (cbsa: string) => void;
}) {
  const { data, loading, error } = useCachedFetch<{ markets: Array<{
    name: string; state: string; cbsa: string;
    metrics: Record<string, { value: number; year: number; unit: string; source: string; yoy_pct: number | null } | null>;
  }>; indicators: Array<{ key: string; label: string; fmt: string }>; }>(
    'me-markets',
    () => axios.get(`${API}/me/markets`, {
      headers: { Authorization: `Bearer ${ctx.token}` },
    }).then(r => r.data),
  );

  const m = data?.markets.find(x => x.cbsa === card.msaCbsa) || data?.markets?.[0];
  const indicators = data?.indicators || [];

  if (loading) return <CardChrome ctx={ctx}>Loading market data…</CardChrome>;
  if (error)   return <CardChrome ctx={ctx}>Error: {error}</CardChrome>;
  if (!m)      return <CardChrome ctx={ctx}>No focused markets configured.</CardChrome>;

  return (
    <CardChrome ctx={ctx}
      title={`${m.name}, ${m.state}`}
      eyebrow="Market snapshot"
      action={data && data.markets.length > 1 ? (
        <select
          value={m.cbsa}
          onChange={e => onPickMsa(e.target.value)}
          onMouseDown={e => e.stopPropagation()}
          style={selStyle(ctx)}>
          {data.markets.map(x => (
            <option key={x.cbsa} value={x.cbsa}>{x.name}, {x.state}</option>
          ))}
        </select>
      ) : null}
    >
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 10, padding: '4px 0',
      }}>
        {indicators.slice(0, 6).map(ind => {
          const v = m.metrics?.[ind.key];
          if (!v || v.value === null) return null;
          const display = fmtMetric(v.value, ind.fmt);
          const yoy = v.yoy_pct;
          return (
            <div key={ind.key} style={{
              borderLeft: `2px solid ${ctx.accent}55`, paddingLeft: 10,
            }}>
              <div style={{
                fontSize: 9, color: ctx.fgMute, fontWeight: 600,
                letterSpacing: '0.08em', textTransform: 'uppercase',
              }}>
                {ind.label}
              </div>
              <div style={{
                fontFamily: 'Fraunces, Georgia, serif',
                fontSize: 15, fontWeight: 700, color: ctx.fg, lineHeight: 1.2,
                marginTop: 1,
              }}>
                {display}
              </div>
              {yoy !== null && yoy !== undefined && (
                <div style={{
                  fontSize: 9, fontWeight: 700,
                  color: yoy > 0 ? '#1A7A4A' : yoy < 0 ? '#CC2200' : ctx.fgMute,
                  marginTop: 1,
                }}>
                  {yoy > 0 ? '+' : ''}{yoy.toFixed(1)}% YoY
                </div>
              )}
            </div>
          );
        })}
      </div>
    </CardChrome>
  );
}

// ── Peer rank card ────────────────────────────────────────────────────

function PeerRankCard({ card, ctx }: { card: BoardCard; ctx: CardCtx }) {
  const sheetId = card.sheetId || 0;
  const { data, loading, error } = useCachedFetch<{
    personas: Array<{
      id: string; label: string; tagline: string; desc: string;
      primary_label: string;
    }>;
    rankings: Record<string, Array<{
      name: string; primary_display: string;
      facts: Array<{ label: string; value: string }>;
    }>>;
  }>(
    `sheets-comparison-${sheetId}`,
    () => axios.get(`${API}/sheets/${sheetId}/comparison`, {
      headers: { Authorization: `Bearer ${ctx.token}` },
    }).then(r => r.data),
  );
  const personaId = card.personaId || 'value';
  const persona = data?.personas.find(p => p.id === personaId);
  const ranking = data?.rankings[personaId] || [];

  if (loading) return <CardChrome ctx={ctx}>Loading peer ranking…</CardChrome>;
  if (error)   return <CardChrome ctx={ctx}>{error}</CardChrome>;
  if (!persona) return <CardChrome ctx={ctx}>No comparison data.</CardChrome>;

  return (
    <CardChrome ctx={ctx}
      title={`Best for ${persona.label}`}
      eyebrow={persona.tagline}
      action={data && data.personas.length > 1 ? (
        <select value={personaId}
          onChange={() => {/* persona picker no-op until parent rewires */}}
          onMouseDown={e => e.stopPropagation()}
          style={selStyle(ctx)}
          disabled>
          <option>{persona.label}</option>
        </select>
      ) : null}
    >
      {/* Explicit 'sorted by' explanation so the user knows what
          'primary_display' on each row actually means */}
      <div style={{
        fontSize: 10, color: ctx.fgMute, marginBottom: 8,
        padding: '5px 8px',
        background: ctx.theme === 'light' ? '#FAFAF9' : '#1A1A20',
        borderRadius: 6, border: `1px solid ${ctx.border}`,
        lineHeight: 1.45,
      }}>
        <span style={{ color: ctx.accent, fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 9,
        }}>Sorted by: </span>
        <span style={{ color: ctx.fg, fontWeight: 600 }}>{persona.primary_label}</span>
        <span> · </span>
        <span>{persona.desc}</span>
      </div>

      <ol style={{
        margin: 0, paddingLeft: 0, listStyle: 'none',
        display: 'flex', flexDirection: 'column', gap: 6,
      }}>
        {ranking.slice(0, 6).map((r, i) => {
          // Pull the most informative facts (rent + sqft + amenities + year)
          const factMap: Record<string, string> = {};
          for (const f of r.facts || []) factMap[f.label] = f.value;
          const supportingFact = factMap['rent'] || factMap['sqft']
                              || factMap['amenities'] || factMap['built'];
          return (
            <li key={i} style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              paddingBottom: 6, borderBottom: `1px dashed ${ctx.border}`,
            }}>
              <span style={{
                fontFamily: 'Fraunces, Georgia, serif',
                color: i < 3 ? ctx.accent : ctx.fgMute,
                fontSize: 12, fontWeight: 800, minWidth: 16,
                lineHeight: 1.3,
              }}>{i + 1}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  color: ctx.fg, fontSize: 12, fontWeight: 600,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{r.name}</div>
                {supportingFact && (
                  <div style={{
                    color: ctx.fgMute, fontSize: 9,
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap', marginTop: 1,
                  }}>{supportingFact}</div>
                )}
              </div>
              <span style={{
                color: ctx.accent, fontFamily: 'Fraunces, Georgia, serif',
                fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
              }}>{r.primary_display}</span>
            </li>
          );
        })}
      </ol>
    </CardChrome>
  );
}

// ── Property card: one building's headline numbers + niche ────────────

function PropertyCard({ card, ctx }: { card: BoardCard; ctx: CardCtx }) {
  const sheetId = card.sheetId || 0;
  const { data, loading, error } = useCachedFetch<{ niches: RichNiche[] }>(
    `sheets-comparison-${sheetId}`,
    () => axios.get(`${API}/sheets/${sheetId}/comparison`, {
      headers: { Authorization: `Bearer ${ctx.token}` },
    }).then(r => r.data),
  );
  const niche = data?.niches.find(n => n.row_index === card.rowIndex)
              || data?.niches?.[0];

  if (loading) return <CardChrome ctx={ctx}>Loading property…</CardChrome>;
  if (error)   return <CardChrome ctx={ctx}>{error}</CardChrome>;
  if (!niche)  return <CardChrome ctx={ctx}>Property not found.</CardChrome>;

  // Build the per-unit-type rent rows the user can actually read.
  const unitRows: Array<{ type: string; rent: string; sqft: string }> = [];
  if (niche.rents && niche.sqfts) {
    if (niche.rents.studio) {
      unitRows.push({
        type:  'Studio',
        rent:  `$${Math.round(niche.rents.studio).toLocaleString()}/mo`,
        sqft:  niche.sqfts.studio ? `${Math.round(niche.sqfts.studio)} sqft` : '—',
      });
    }
    if (niche.rents['1b1b']) {
      unitRows.push({
        type:  '1B1B',
        rent:  `$${Math.round(niche.rents['1b1b']).toLocaleString()}/mo`,
        sqft:  niche.sqfts['1b1b'] ? `${Math.round(niche.sqfts['1b1b'])} sqft` : '—',
      });
    }
    if (niche.rents['2b2b']) {
      unitRows.push({
        type:  '2B2B',
        rent:  `$${Math.round(niche.rents['2b2b']).toLocaleString()}/mo`,
        sqft:  niche.sqfts['2b2b'] ? `${Math.round(niche.sqfts['2b2b'])} sqft` : '—',
      });
    }
  }

  return (
    <CardChrome ctx={ctx} title={niche.name} eyebrow={niche.location || 'Property'}>
      {/* Niche tags */}
      {niche.leading.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {niche.leading.map((lead, i) => (
            <div key={i} style={{ marginBottom: 4 }}>
              <span style={{
                background: ctx.accent + '22', color: ctx.accent,
                fontSize: 8, fontWeight: 700,
                letterSpacing: '0.14em', textTransform: 'uppercase',
                padding: '2px 8px', borderRadius: 4,
              }}>{lead.label}</span>
              <span style={{
                marginLeft: 6, color: ctx.fgMute, fontSize: 10, fontWeight: 400,
              }}>{lead.detail}</span>
            </div>
          ))}
        </div>
      )}

      {/* PER-UNIT RENT TABLE — fixes the 'rent for what?' ambiguity */}
      {unitRows.length > 0 && (
        <div style={{
          background: ctx.theme === 'light' ? '#FAFAF9' : '#1A1A20',
          border: `1px solid ${ctx.border}`, borderRadius: 8,
          padding: '6px 10px', marginBottom: 8,
        }}>
          {unitRows.map((u, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: '50px 1fr 1fr',
              gap: 8, padding: '4px 0',
              borderBottom: i < unitRows.length - 1 ? `1px solid ${ctx.border}55` : 'none',
              alignItems: 'baseline',
            }}>
              <div style={{
                fontSize: 9, color: ctx.fgMute, fontWeight: 700,
                letterSpacing: '0.1em', textTransform: 'uppercase',
              }}>{u.type}</div>
              <div style={{
                fontFamily: 'Fraunces, Georgia, serif',
                fontSize: 12, fontWeight: 700, color: ctx.fg,
              }}>{u.rent}</div>
              <div style={{
                fontSize: 10, color: ctx.fgMute, textAlign: 'right',
              }}>{u.sqft}</div>
            </div>
          ))}
        </div>
      )}

      {/* Bottom stats */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6,
        paddingTop: 6, borderTop: `1px solid ${ctx.border}`,
      }}>
        {niche.newness !== null && (
          <Stat label="built" value={String(Math.round(niche.newness))} ctx={ctx} />
        )}
        {niche.amenity !== null && (
          <Stat label="amenities" value={String(Math.round(niche.amenity))} ctx={ctx} />
        )}
        {niche.income_min !== null && (
          <Stat label="income" value={`$${Math.round(niche.income_min / 1000)}k/yr`} ctx={ctx} />
        )}
      </div>
    </CardChrome>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  VOICE MEMOS — Apple-Voice-Memos-inspired recording surface with a live
//  AnalyserNode-driven waveform + Web Speech API transcription. Stop →
//  AI parses transcript via POST /me/voice-parse → returns structured
//  property data → drop on board.
// ═══════════════════════════════════════════════════════════════════════

interface VoiceParseResult {
  transcript: string;
  extracted: BoardCard['voice'] extends infer V
    ? V extends { extracted: infer E } ? E : null
    : null;
  entities?: VoiceMemoRow['entities'];
  parse_error?: string;
}

type VoiceDestination =
  | { kind: 'new-card' }
  | { kind: 'new-table'; title: string }
  | { kind: 'append'; tableId: string };

function VoiceRecorderOverlay({ token, theme, existingTables, onCancel, onComplete }: {
  token:          string;
  theme:          Theme;
  existingTables: Array<{ id: string; title: string; count: number }>;
  onCancel:       () => void;
  onComplete:     (payload: VoiceParseResult, destination: VoiceDestination) => void;
}) {
  const [status, setStatus] = useState<'idle' | 'recording' | 'parsing' | 'picking' | 'error'>('idle');
  const [parsedResult, setParsedResult] = useState<VoiceParseResult | null>(null);
  const [newTableTitle, setNewTableTitle] = useState('Property memos');
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim]       = useState('');
  const [error, setError]           = useState<string | null>(null);
  const [supported, setSupported]   = useState<boolean | null>(null);

  // Live waveform: 32 bar heights computed from AnalyserNode
  const [bars, setBars] = useState<number[]>(new Array(32).fill(2));

  const audioCtxRef     = useRef<AudioContext | null>(null);
  const analyserRef     = useRef<AnalyserNode | null>(null);
  const sourceRef       = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef       = useRef<MediaStream | null>(null);
  const rafRef          = useRef<number | null>(null);
  const recognitionRef  = useRef<unknown | null>(null);
  const finalRef        = useRef('');
  const startedAtRef    = useRef<number>(Date.now());

  // Theme tokens
  const isDark = theme === 'dark';
  const overlayBg = isDark
    ? 'radial-gradient(circle at 50% 30%, #1A1A22, #08080C 60%)'
    : 'radial-gradient(circle at 50% 30%, #18181F, #050507 60%)';
  const accent = '#C9A96E';

  // Detect Web Speech API support on mount
  useEffect(() => {
    const SR = (window as unknown as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown })
      .SpeechRecognition || (window as unknown as { webkitSpeechRecognition?: unknown }).webkitSpeechRecognition;
    setSupported(!!SR);
  }, []);

  const cleanup = useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try {
      const rec = recognitionRef.current as { stop?: () => void } | null;
      rec?.stop?.();
    } catch { /* ignore */ }
    recognitionRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* ignore */ }
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    sourceRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => () => cleanup(), [cleanup]);

  const startRecording = async () => {
    setError(null);
    finalRef.current = '';
    setTranscript('');
    setInterim('');
    startedAtRef.current = Date.now();

    if (!supported) {
      setError("Your browser doesn't support live speech recognition. Use Chrome, Edge, or Safari.");
      setStatus('error');
      return;
    }

    // Mic permission + audio nodes for waveform
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const Ctx = (window.AudioContext
        || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      sourceRef.current = src;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      analyserRef.current = analyser;

      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        // Down-sample 128 bins → 32 bars
        const step = Math.floor(data.length / 32);
        const next: number[] = [];
        for (let i = 0; i < 32; i++) {
          let sum = 0;
          for (let j = 0; j < step; j++) sum += data[i * step + j];
          next.push(sum / step);
        }
        setBars(next);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e: unknown) {
      const msg = (e as Error)?.message || 'Microphone access denied.';
      setError(msg);
      setStatus('error');
      cleanup();
      return;
    }

    // Speech recognition
    try {
      const SR = (window as unknown as { SpeechRecognition?: new () => unknown; webkitSpeechRecognition?: new () => unknown })
        .SpeechRecognition || (window as unknown as { webkitSpeechRecognition?: new () => unknown }).webkitSpeechRecognition;
      if (!SR) throw new Error('SpeechRecognition unavailable');
      const rec = new (SR as new () => {
        continuous: boolean; interimResults: boolean; lang: string;
        onresult: (e: { results: ArrayLike<ArrayLike<{ transcript: string; isFinal?: boolean }> & { isFinal?: boolean }>; resultIndex: number }) => void;
        onerror: (e: { error: string }) => void;
        onend: () => void;
        start: () => void;
        stop: () => void;
      })();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = 'en-US';
      rec.onresult = (e) => {
        let interimChunk = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const r = e.results[i];
          const text = r[0].transcript;
          if ((r as { isFinal?: boolean }).isFinal) {
            finalRef.current += text + ' ';
          } else {
            interimChunk += text;
          }
        }
        setTranscript(finalRef.current);
        setInterim(interimChunk);
      };
      rec.onerror = (e) => {
        if (e.error === 'no-speech' || e.error === 'aborted') return;
        setError(`Speech error: ${e.error}`);
      };
      rec.onend = () => {
        // If we're still in recording status, restart (continuous fix for Chrome quirks)
        if (status === 'recording' && recognitionRef.current) {
          try { (recognitionRef.current as { start: () => void }).start(); } catch { /* ignore */ }
        }
      };
      rec.start();
      recognitionRef.current = rec;
      setStatus('recording');
    } catch (e: unknown) {
      const msg = (e as Error)?.message || 'Could not start speech recognition.';
      setError(msg);
      setStatus('error');
      cleanup();
    }
  };

  const stopRecording = async () => {
    cleanup();
    const finalTranscript = (finalRef.current + ' ' + interim).trim();
    if (!finalTranscript) {
      setError("Didn't catch any speech. Try again.");
      setStatus('error');
      return;
    }
    setStatus('parsing');
    try {
      const r = await axios.post(`${API}/me/voice-parse`,
        { transcript: finalTranscript },
        { headers: { Authorization: `Bearer ${token}` } });
      setParsedResult({
        transcript: finalTranscript,
        extracted:  r.data?.extracted ?? null,
        // Capture the verbatim entities returned in the same Claude
        // call — saves a separate /me/extract-entities round-trip.
        entities:   r.data?.entities ?? undefined,
        parse_error: r.data?.parse_error,
      });
      setStatus('picking');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
                || (e as Error)?.message || 'Parsing failed.';
      // Preserve recording — still let user place it
      setParsedResult({
        transcript: finalTranscript,
        extracted: null,
        parse_error: String(msg),
      });
      setStatus('picking');
    }
  };

  const place = (destination: VoiceDestination) => {
    if (!parsedResult) return;
    onComplete(parsedResult, destination);
  };

  // Auto-start on mount once support is detected
  useEffect(() => {
    if (supported && status === 'idle') {
      startRecording();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported]);

  const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
  const mins = Math.floor(elapsed / 60).toString().padStart(2, '0');
  const secs = (elapsed % 60).toString().padStart(2, '0');

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 40,
      background: overlayBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'coastFadeIn 0.3s ease-out',
    }}>
      <div style={{
        width: '100%', maxWidth: 720, padding: '0 40px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28,
      }}>
        {/* Eyebrow */}
        <div style={{
          color: accent, fontSize: 10, fontWeight: 700,
          letterSpacing: '0.32em', textTransform: 'uppercase',
        }}>
          Voice memo
        </div>

        {/* Timer */}
        <div style={{
          fontFamily: 'Fraunces, Georgia, serif',
          fontSize: 56, fontWeight: 300, color: '#FFFFFF',
          letterSpacing: '0.04em', lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          textShadow: '0 0 24px rgba(201, 169, 110, 0.18)',
        }}>
          {status === 'parsing' ? '…' : `${mins}:${secs}`}
        </div>

        {/* Waveform */}
        <div style={{
          width: '100%', height: 120,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 4,
        }}>
          {bars.map((v, i) => {
            const h = Math.max(4, Math.min(110, v * 0.85));
            return (
              <div key={i}
                style={{
                  width: 6, height: h,
                  background: status === 'recording'
                    ? `linear-gradient(180deg, ${accent}, ${accent}99)`
                    : '#3A3A45',
                  borderRadius: 3,
                  boxShadow: status === 'recording'
                    ? `0 0 ${4 + (v / 255) * 10}px ${accent}55`
                    : 'none',
                  transition: 'height 80ms ease-out, background 0.2s',
                }} />
            );
          })}
        </div>

        {/* Live transcript */}
        <div style={{
          width: '100%', minHeight: 60, maxHeight: 160, overflowY: 'auto',
          padding: '14px 20px',
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 14,
          fontSize: 15, color: '#E5E5EA', lineHeight: 1.6,
          fontWeight: 300,
          textAlign: transcript || interim ? 'left' : 'center',
        }}>
          {transcript && <span>{transcript}</span>}
          {interim && (
            <span style={{ color: 'rgba(255,255,255,0.45)' }}>{interim}</span>
          )}
          {!transcript && !interim && status === 'recording' && (
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>
              Speak naturally — "Three-unit property in Brickell, cash flow $1,200/mo, cap rate around 7.1, needs roof replacement…"
            </span>
          )}
          {status === 'idle' && supported && (
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>
              Requesting microphone access…
            </span>
          )}
          {supported === false && (
            <span style={{ color: '#FFB3B3' }}>
              Your browser doesn't support live speech recognition. Use Chrome, Edge, or Safari.
            </span>
          )}
        </div>

        {error && (
          <div style={{
            color: '#FFB3B3', fontSize: 12, textAlign: 'center', maxWidth: 460,
            lineHeight: 1.5,
          }}>
            {error}
          </div>
        )}

        {/* Controls */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 24, marginTop: 8,
        }}>
          <button onClick={() => { cleanup(); onCancel(); }}
            style={{
              background: 'transparent', border: 'none',
              color: 'rgba(255,255,255,0.5)', fontSize: 12,
              letterSpacing: '0.16em', textTransform: 'uppercase',
              cursor: 'pointer', padding: '12px 16px', fontWeight: 500,
              fontFamily: 'inherit',
            }}>
            Cancel
          </button>

          {/* Big round stop button */}
          <button
            onClick={status === 'recording' ? stopRecording : (status === 'error' ? () => { setStatus('idle'); setError(null); startRecording(); } : undefined)}
            disabled={status === 'parsing'}
            style={{
              width: 84, height: 84, borderRadius: 42,
              background: status === 'parsing' ? '#666' : '#FF3B30',
              border: '4px solid rgba(255,255,255,0.12)',
              cursor: status === 'parsing' ? 'default' : 'pointer',
              boxShadow: status === 'recording'
                ? '0 0 0 8px rgba(255, 59, 48, 0.18), 0 0 40px rgba(255, 59, 48, 0.45)'
                : '0 8px 24px rgba(0,0,0,0.4)',
              transition: 'all 0.2s',
              animation: status === 'recording' ? 'coastPulse 1.6s ease-in-out infinite' : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
            {status === 'parsing' ? (
              <span style={{ color: '#fff', fontSize: 11, fontWeight: 700,
                letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Parsing
              </span>
            ) : status === 'error' ? (
              <span style={{ color: '#fff', fontSize: 11, fontWeight: 700,
                letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Retry
              </span>
            ) : (
              <div style={{
                width: 28, height: 28, background: '#fff', borderRadius: 4,
              }} />
            )}
          </button>

          <div style={{ width: 72 }} /> {/* spacer to balance Cancel */}
        </div>

        <div style={{
          fontSize: 10, color: 'rgba(255,255,255,0.35)',
          letterSpacing: '0.1em',
        }}>
          {status === 'recording' && 'Click the red button to stop and parse'}
          {status === 'parsing'   && 'AI is extracting structured property data…'}
        </div>
      </div>

      {/* Destination picker — appears AFTER parsing, before placing */}
      {status === 'picking' && parsedResult && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 32, animation: 'coastFadeIn 0.2s ease-out',
          backdropFilter: 'blur(8px)',
        }}>
          <div style={{
            background: theme === 'light' ? '#FFFFFF' : '#1A1A20',
            color: theme === 'light' ? '#1A1A1A' : '#F0F0F0',
            border: `1px solid ${theme === 'light' ? '#E5E5E5' : '#2A2A30'}`,
            borderRadius: 20, padding: 28, maxWidth: 540, width: '100%',
            boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
          }}>
            <div style={{
              color: accent, fontSize: 10, fontWeight: 700,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              marginBottom: 6,
            }}>
              Where should this go?
            </div>
            <div style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: 22, fontWeight: 700, marginBottom: 6,
            }}>
              {parsedResult.extracted?.property_name
                || parsedResult.extracted?.summary
                || 'Voice memo'}
            </div>
            {parsedResult.extracted?.summary && (
              <div style={{
                color: theme === 'light' ? '#6B7280' : '#9CA3AF',
                fontSize: 12, lineHeight: 1.55, marginBottom: 16,
                fontStyle: 'italic',
              }}>
                "{parsedResult.extracted.summary}"
              </div>
            )}
            {parsedResult.parse_error && (
              <div style={{
                background: '#CC220011', border: '1px solid #CC220044',
                borderRadius: 8, padding: '8px 12px', marginBottom: 12,
                fontSize: 11, color: '#CC2200',
              }}>
                AI parsing returned: {parsedResult.parse_error}. Transcript
                is preserved either way.
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Append to existing tables */}
              {existingTables.map(t => (
                <button key={t.id}
                  onClick={() => place({ kind: 'append', tableId: t.id })}
                  style={pickerBtnStyle(theme, accent, false)}>
                  <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 14, fontWeight: 700 }}>
                    Add to "{t.title}"
                  </span>
                  <span style={{ color: accent, fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.1em', marginLeft: 8 }}>
                    {t.count} row{t.count === 1 ? '' : 's'}
                  </span>
                </button>
              ))}

              {/* New table */}
              <div style={{
                display: 'flex', gap: 8, alignItems: 'stretch',
              }}>
                <input
                  value={newTableTitle}
                  onChange={e => setNewTableTitle(e.target.value)}
                  placeholder="Table name"
                  style={{
                    flex: 1,
                    background: theme === 'light' ? '#FAFAF9' : '#0F0F12',
                    color: theme === 'light' ? '#1A1A1A' : '#F0F0F0',
                    border: `1px solid ${theme === 'light' ? '#E5E5E5' : '#2A2A30'}`,
                    borderRadius: 10, padding: '8px 12px',
                    fontSize: 13, fontFamily: 'inherit',
                    outline: 'none',
                  }} />
                <button
                  onClick={() => place({ kind: 'new-table',
                    title: newTableTitle.trim() || 'Property memos' })}
                  style={pickerBtnStyle(theme, accent, true)}>
                  New spreadsheet
                </button>
              </div>

              {/* Single card */}
              <button
                onClick={() => place({ kind: 'new-card' })}
                style={pickerBtnStyle(theme, accent, false)}>
                <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 14, fontWeight: 500 }}>
                  Drop as a single card
                </span>
              </button>
            </div>

            <button onClick={onCancel}
              style={{
                marginTop: 16, background: 'transparent', border: 'none',
                color: theme === 'light' ? '#6B7280' : '#9CA3AF',
                fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase',
                cursor: 'pointer', fontFamily: 'inherit',
                padding: '6px 0',
              }}>
              Discard recording
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function pickerBtnStyle(theme: Theme, accent: string, primary: boolean): React.CSSProperties {
  return {
    background:  primary ? accent : (theme === 'light' ? '#FAFAF9' : '#252530'),
    color:       primary ? '#FFFFFF' : (theme === 'light' ? '#1A1A1A' : '#F0F0F0'),
    border:      `1px solid ${primary ? accent : (theme === 'light' ? '#E5E5E5' : '#2A2A30')}`,
    borderRadius: 10, padding: '12px 16px',
    fontSize: 13, fontWeight: primary ? 700 : 500,
    cursor: 'pointer', textAlign: 'left',
    display: 'flex', alignItems: 'center',
    fontFamily: 'inherit', transition: 'all 0.15s',
  };
}

// ── Voice memo card: structured property data + raw transcript ────────

/* ── MeetingCard ────────────────────────────────────────────────────────
 *  Compact read-only render of a sit-in meeting. Header shows duration +
 *  date. Click "details" to expand: summary, speakers, topics, decisions,
 *  action items, open questions, risks, entities, full transcript.
 *
 *  Address entities are clickable and deeplink to /map?intel_address=...,
 *  consistent with the voice-memo EntityChips behaviour.
 */
function MeetingCard({ card, ctx, onUpdate }: {
  card: BoardCard; ctx: CardCtx;
  onUpdate?: (patch: Partial<BoardCard>) => void;
}) {
  const m = card.meeting;
  const [expanded, setExpanded] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  // Deck viewer state — opens a fullscreen pitch-deck modal built
  // deterministically from this meeting's parsed payload. No extra
  // AI inference happens; "Generate deck" just lays out what's
  // already there.
  const [deckOpen, setDeckOpen] = useState(false);
  // Inline transcript editor (item 4). If Claude misheard a word or
  // a name, the user can fix it and re-parse — the structured payload
  // and entity chips update from the corrected source.
  const [editing, setEditing] = useState(false);
  const [draftTranscript, setDraftTranscript] = useState('');
  const [reparseBusy, setReparseBusy] = useState(false);
  const [reparseError, setReparseError] = useState<string | null>(null);

  if (!m) {
    return (
      <div style={{
        padding: 16, color: ctx.fgMute, fontSize: 12, fontStyle: 'italic',
      }}>
        Meeting card has no data.
      </div>
    );
  }

  const dur = m.duration_sec || 0;
  const durLabel = (() => {
    const h = Math.floor(dur / 3600);
    const mn = Math.floor((dur % 3600) / 60);
    const s = dur % 60;
    return h > 0 ? `${h}h ${mn}m` : mn > 0 ? `${mn}m ${s}s` : `${s}s`;
  })();
  const recordedAt = (() => {
    try {
      return new Date(m.recorded_at).toLocaleString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    } catch { return m.recorded_at; }
  })();
  const p = m.parsed;

  const openOnMap = (addr: string) => {
    const url = `/map?intel_address=${encodeURIComponent(addr)}`;
    window.history.pushState({}, '', url);
    window.dispatchEvent(new Event('popstate'));
  };

  return (
    <div style={{
      padding: '14px 18px', overflowY: 'auto',
      display: 'flex', flexDirection: 'column', gap: 10, flex: 1,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 10,
      }}>
        <div style={{
          fontSize: 9, color: ctx.accent, fontWeight: 700,
          letterSpacing: '0.22em', textTransform: 'uppercase',
        }}>
          Meeting
        </div>
        <div style={{
          fontSize: 10, color: ctx.fgMute, letterSpacing: '0.06em',
        }}>
          {durLabel} · {recordedAt}
        </div>
        <span style={{ flex: 1 }} />
        {/* Generate deck — turns parsed meeting into a slide deck.
            Disabled when the meeting has no parsed data (rare but
            possible if the parse step failed). */}
        <button
          onClick={(e) => { e.stopPropagation(); setDeckOpen(true); }}
          onMouseDown={(e) => e.stopPropagation()}
          disabled={!m.parsed}
          title={m.parsed
            ? 'Generate a pitch deck from this meeting'
            : 'No parsed data — can\'t generate a deck'}
          style={{
            background: m.parsed ? ctx.accent : 'transparent',
            border: `1px solid ${ctx.accent}`,
            color: m.parsed ? '#0a1322' : ctx.fgMute,
            fontSize: 9, fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            padding: '3px 8px', borderRadius: 5,
            cursor: m.parsed ? 'pointer' : 'not-allowed',
            opacity: m.parsed ? 1 : 0.5,
            fontFamily: 'inherit',
          }}>
          Generate deck
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            background: 'transparent', border: `1px solid ${ctx.border}`,
            color: ctx.fgMute, fontSize: 9, fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            padding: '3px 8px', borderRadius: 5, cursor: 'pointer',
            fontFamily: 'inherit',
          }}>
          {expanded ? 'Collapse' : 'Details'}
        </button>
      </div>
      {deckOpen && (
        <DeckViewer
          meeting={{
            recorded_at:  m.recorded_at,
            duration_sec: m.duration_sec,
            transcript:   m.transcript,
            parsed:       m.parsed,
          }}
          token={ctx.token}
          onClose={() => setDeckOpen(false)}
        />
      )}

      {/* Summary (always visible) */}
      <div className="lib-fraunces" style={{
        fontFamily: 'Fraunces, Georgia, serif', fontSize: 16, color: ctx.fg,
        lineHeight: 1.35,
      }}>
        {p?.summary || <span style={{ color: ctx.fgMute, fontStyle: 'italic' }}>No summary captured.</span>}
      </div>

      {/* Quick counts row (always visible) */}
      {p && (
        <div style={{
          display: 'flex', gap: 16, flexWrap: 'wrap',
          fontSize: 10, color: ctx.fgMute, letterSpacing: '0.04em',
        }}>
          <MeetCount label="Speakers"   n={p.speakers?.length || 0} />
          <MeetCount label="Topics"     n={p.topics?.length || 0} />
          <MeetCount label="Decisions"  n={p.decisions?.length || 0} />
          <MeetCount label="Actions"    n={p.action_items?.length || 0} />
          <MeetCount label="Open Qs"    n={p.open_questions?.length || 0} />
          <MeetCount label="Risks"      n={p.risks_flagged?.length || 0} />
        </div>
      )}

      {expanded && p && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 4 }}>
          {p.speakers?.length > 0 && (
            <MeetSection ctx={ctx} label="Speakers" right="AI-guessed">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {p.speakers.map(s => (
                  <span key={s.id} style={{
                    background: ctx.theme === 'light' ? '#F8F8F8' : '#1a2438',
                    border: `1px solid ${ctx.border}`,
                    fontSize: 11, padding: '3px 9px', borderRadius: 999,
                    color: ctx.fg,
                  }}>
                    {s.name || s.id}
                    {s.role_guess && <span style={{ color: ctx.fgMute }}> · {s.role_guess}</span>}
                    <span style={{ color: ctx.fgMute }}> · {Math.round(s.share_pct)}%</span>
                  </span>
                ))}
              </div>
            </MeetSection>
          )}

          {p.topics?.length > 0 && (
            <MeetSection ctx={ctx} label="Topics">
              <ol style={{ margin: 0, paddingLeft: 16, color: ctx.fg, fontSize: 12, lineHeight: 1.55 }}>
                {p.topics.map((t, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    <span style={{ fontWeight: 600 }}>{t.label}</span>
                    {t.summary && <span style={{ color: ctx.fgMute }}> — {t.summary}</span>}
                  </li>
                ))}
              </ol>
            </MeetSection>
          )}

          {p.decisions?.length > 0 && (
            <MeetSection ctx={ctx} label="Decisions">
              <MeetQuoteList ctx={ctx}
                items={p.decisions.map(d => ({ primary: d.decision, quote: d.quote }))} />
            </MeetSection>
          )}

          {p.action_items?.length > 0 && (
            <MeetSection ctx={ctx} label="Action items">
              <MeetQuoteList ctx={ctx}
                items={p.action_items.map(a => ({
                  primary: a.action,
                  meta: [a.owner && `Owner: ${a.owner}`, a.deadline && `Due: ${a.deadline}`]
                          .filter(Boolean).join(' · '),
                  quote: a.quote,
                }))} />
            </MeetSection>
          )}

          {p.open_questions?.length > 0 && (
            <MeetSection ctx={ctx} label="Open questions">
              <MeetQuoteList ctx={ctx}
                items={p.open_questions.map(q => ({ primary: q.question, quote: q.quote }))} />
            </MeetSection>
          )}

          {p.risks_flagged?.length > 0 && (
            <MeetSection ctx={ctx} label="Risks flagged">
              <MeetQuoteList ctx={ctx}
                items={p.risks_flagged.map(r => ({ primary: r.risk, quote: r.quote }))} />
            </MeetSection>
          )}

          {(() => {
            const e = p.entities;
            if (!e) return null;
            const total = e.addresses.length + e.properties.length + e.tribes.length
                        + e.regulations.length + e.people.length + e.money.length;
            if (total === 0) return null;
            return (
              <MeetSection ctx={ctx} label="Mentioned">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {e.addresses.length > 0 && (
                    <MeetEntityRow ctx={ctx} label="Address" items={e.addresses}
                      actionable onClick={openOnMap} />
                  )}
                  {e.properties.length > 0 && <MeetEntityRow ctx={ctx} label="Property" items={e.properties} />}
                  {e.tribes.length > 0 && <MeetEntityRow ctx={ctx} label="Tribe" items={e.tribes} />}
                  {e.regulations.length > 0 && <MeetEntityRow ctx={ctx} label="Reg" items={e.regulations} />}
                  {e.people.length > 0 && <MeetEntityRow ctx={ctx} label="Person" items={e.people} />}
                  {e.money.length > 0 && <MeetEntityRow ctx={ctx} label="$" items={e.money} />}
                </div>
              </MeetSection>
            );
          })()}

          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button
              onClick={(ev) => { ev.stopPropagation(); setShowTranscript(s => !s); }}
              onMouseDown={(ev) => ev.stopPropagation()}
              style={{
                background: 'transparent', border: 'none', color: ctx.accent,
                fontSize: 9, fontWeight: 700, letterSpacing: '0.12em',
                textTransform: 'uppercase', cursor: 'pointer', padding: 0,
                textAlign: 'left', fontFamily: 'inherit',
              }}>
              {showTranscript ? '▼ Hide transcript' : '▶ Show full transcript'}
            </button>
            {showTranscript && onUpdate && !editing && (
              <button
                onClick={(ev) => {
                  ev.stopPropagation();
                  setDraftTranscript(m.transcript || '');
                  setReparseError(null);
                  setEditing(true);
                }}
                onMouseDown={(ev) => ev.stopPropagation()}
                style={{
                  background: 'transparent', border: `1px solid ${ctx.border}`,
                  color: ctx.fgMute, fontSize: 9, fontWeight: 700,
                  letterSpacing: '0.10em', textTransform: 'uppercase',
                  padding: '2px 7px', borderRadius: 4, cursor: 'pointer',
                  fontFamily: 'inherit',
                }}>
                Edit & re-parse
              </button>
            )}
          </div>
          {showTranscript && !editing && (
            <div
              onMouseDown={(ev) => ev.stopPropagation()}
              style={{
                fontSize: 11, color: ctx.fg, lineHeight: 1.55,
                background: ctx.theme === 'light' ? '#FFFFFF' : '#0F0F12',
                border: `1px solid ${ctx.border}`,
                borderRadius: 6, padding: 10, fontStyle: 'italic',
                maxHeight: 220, overflowY: 'auto', whiteSpace: 'pre-wrap',
              }}>
              "{m.transcript}"
            </div>
          )}
          {showTranscript && editing && (
            <div onMouseDown={(ev) => ev.stopPropagation()}
                 style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <textarea
                value={draftTranscript}
                onChange={(e) => setDraftTranscript(e.target.value)}
                style={{
                  width: '100%', minHeight: 140, maxHeight: 280,
                  resize: 'vertical',
                  background: ctx.theme === 'light' ? '#FFFFFF' : '#0F0F12',
                  color: ctx.fg, border: `1px solid ${ctx.accent}55`,
                  borderRadius: 6, padding: 10, fontSize: 11, lineHeight: 1.55,
                  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                  outline: 'none',
                }}
              />
              {reparseError && (
                <div style={{ color: '#CC2200', fontSize: 10, fontStyle: 'italic' }}>
                  {reparseError}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={(ev) => { ev.stopPropagation(); setEditing(false); setReparseError(null); }}
                  onMouseDown={(ev) => ev.stopPropagation()}
                  disabled={reparseBusy}
                  style={{
                    background: 'transparent', border: `1px solid ${ctx.border}`,
                    color: ctx.fgMute, fontSize: 9, fontWeight: 700,
                    letterSpacing: '0.10em', textTransform: 'uppercase',
                    padding: '4px 10px', borderRadius: 4,
                    cursor: reparseBusy ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit', opacity: reparseBusy ? 0.5 : 1,
                  }}>
                  Cancel
                </button>
                <button
                  onClick={async (ev) => {
                    ev.stopPropagation();
                    const text = draftTranscript.trim();
                    if (!text) {
                      setReparseError('Transcript cannot be empty.');
                      return;
                    }
                    setReparseBusy(true);
                    setReparseError(null);
                    try {
                      const r = await axios.post(`${API}/me/meeting-parse`, {
                        transcript:       text,
                        duration_seconds: m.duration_sec,
                      }, {
                        headers: { Authorization: `Bearer ${ctx.token}` },
                        timeout: 180_000,
                      });
                      const data = r.data as { transcript: string; parsed: any };
                      if (!data.parsed) {
                        setReparseError('Parse returned no structured data. Edit not saved.');
                        return;
                      }
                      // Persist the edited transcript + fresh parse back to
                      // the card via the parent updateCard. Auto-save then
                      // pushes the new state to /boards.
                      onUpdate?.({
                        meeting: {
                          ...m,
                          transcript: data.transcript,
                          parsed:     data.parsed,
                        },
                      });
                      setEditing(false);
                    } catch (e: any) {
                      const detail: string = e?.response?.data?.detail
                                  || e?.response?.data?.message
                                  || e?.message
                                  || 'Re-parse failed';
                      // Friendly Anthropic credit message, same as the
                      // sit-in listener.
                      if (/credit balance is too low|insufficient_credit/i.test(detail)) {
                        setReparseError('Anthropic credits empty. Add credits at console.anthropic.com.');
                      } else {
                        setReparseError(detail);
                      }
                    } finally {
                      setReparseBusy(false);
                    }
                  }}
                  onMouseDown={(ev) => ev.stopPropagation()}
                  disabled={reparseBusy}
                  style={{
                    background: ctx.accent, color: '#0a1322', border: 'none',
                    fontSize: 9, fontWeight: 700, letterSpacing: '0.10em',
                    textTransform: 'uppercase',
                    padding: '4px 12px', borderRadius: 4,
                    cursor: reparseBusy ? 'wait' : 'pointer',
                    fontFamily: 'inherit', opacity: reparseBusy ? 0.7 : 1,
                  }}>
                  {reparseBusy ? 'Re-parsing…' : 'Save & re-parse'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MeetCount({ label, n }: { label: string; n: number }) {
  return (
    <span>
      <b style={{ fontVariantNumeric: 'tabular-nums' }}>{n}</b>
      <span style={{ marginLeft: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
    </span>
  );
}

function MeetSection({ ctx, label, right, children }: {
  ctx: CardCtx; label: string; right?: string; children: React.ReactNode;
}) {
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 5,
      }}>
        <div style={{
          fontSize: 8, color: ctx.accent, fontWeight: 700,
          letterSpacing: '0.20em', textTransform: 'uppercase',
        }}>
          {label}
        </div>
        {right && (
          <span style={{ fontSize: 8, color: ctx.fgMute, fontStyle: 'italic',
                          letterSpacing: '0.06em' }}>
            {right}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function MeetQuoteList({ ctx, items }: {
  ctx: CardCtx;
  items: Array<{ primary: string; meta?: string; quote?: string }>;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {items.map((it, i) => (
        <div key={i} style={{
          padding: '7px 10px', background: ctx.theme === 'light' ? '#F8F8F8' : '#1a2438',
          border: `1px solid ${ctx.border}`, borderRadius: 5,
        }}>
          <div style={{ color: ctx.fg, fontSize: 12, lineHeight: 1.4 }}>{it.primary}</div>
          {it.meta && (
            <div style={{ color: ctx.accent, fontSize: 9, marginTop: 2, letterSpacing: '0.04em' }}>
              {it.meta}
            </div>
          )}
          {it.quote && (
            <div style={{
              marginTop: 4, paddingLeft: 7, borderLeft: `2px solid ${ctx.border}`,
              color: ctx.fgMute, fontSize: 10, fontStyle: 'italic', lineHeight: 1.55,
            }}>
              "{it.quote}"
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function MeetEntityRow({ ctx, label, items, actionable, onClick }: {
  ctx: CardCtx; label: string; items: string[];
  actionable?: boolean; onClick?: (item: string) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, flexWrap: 'wrap' }}>
      <div style={{
        flexShrink: 0, fontSize: 8, color: ctx.fgMute, fontWeight: 700,
        letterSpacing: '0.10em', textTransform: 'uppercase',
        minWidth: 56, paddingTop: 2,
      }}>
        {label}
      </div>
      <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {items.map((it, i) => (
          <button
            key={`${label}-${i}`}
            onClick={(ev) => { ev.stopPropagation(); if (actionable && onClick) onClick(it); }}
            onMouseDown={(ev) => ev.stopPropagation()}
            disabled={!actionable}
            style={{
              fontSize: 10, padding: '2px 8px', borderRadius: 999,
              fontFamily: 'inherit',
              border: `1px solid ${actionable ? ctx.accent : ctx.border}`,
              background: actionable
                ? (ctx.theme === 'light' ? 'rgba(201,169,110,0.10)' : 'rgba(201,169,110,0.15)')
                : 'transparent',
              color: actionable ? ctx.accent : ctx.fg,
              cursor: actionable ? 'pointer' : 'default',
            }}
          >
            {it}{actionable ? ' →' : ''}
          </button>
        ))}
      </div>
    </div>
  );
}


function VoiceMemoCard({ card, ctx, onUpdate }: {
  card: BoardCard; ctx: CardCtx;
  onUpdate?: (patch: Partial<BoardCard>) => void;
}) {
  const [showTranscript, setShowTranscript] = useState(false);
  // Deck viewer state. Pure deterministic deck from the memo's already-
  // parsed payload — same constraint as meeting decks (no extra AI).
  const [deckOpen, setDeckOpen] = useState(false);
  // Inline transcript editor (item 4 — same shape as MeetingCard).
  const [editing, setEditing] = useState(false);
  const [draftTranscript, setDraftTranscript] = useState('');
  const [reparseBusy, setReparseBusy] = useState(false);
  const [reparseError, setReparseError] = useState<string | null>(null);
  const v = card.voice;
  if (!v) {
    return <CardChrome ctx={ctx}>No voice data.</CardChrome>;
  }
  const ex = v.extracted;
  const summary = ex?.summary
                || ex?.property_name
                || (v.transcript.length > 40 ? v.transcript.slice(0, 60) + '…' : v.transcript);
  const recordedDate = new Date(v.recorded_at);
  const recordedLabel = recordedDate.toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });

  // Build a list of populated fields
  const rows: Array<{ label: string; value: string }> = [];
  if (ex) {
    if (ex.location)          rows.push({ label: 'location',  value: ex.location });
    if (ex.address)           rows.push({ label: 'address',   value: ex.address });
    if (ex.property_type)     rows.push({ label: 'type',      value: ex.property_type });
    if (ex.units !== null && ex.units !== undefined)
                              rows.push({ label: 'units',     value: String(ex.units) });
    if (ex.year_built)        rows.push({ label: 'built',     value: String(ex.year_built) });
    if (ex.sqft)              rows.push({ label: 'sqft',      value: `${ex.sqft.toLocaleString()} sqft` });
    if (ex.purchase_price)    rows.push({ label: 'price',     value: `$${ex.purchase_price.toLocaleString()}` });
    if (ex.rent_monthly)      rows.push({ label: 'rent',      value: `$${ex.rent_monthly.toLocaleString()}/mo` });
    if (ex.cap_rate !== null && ex.cap_rate !== undefined)
                              rows.push({ label: 'cap rate',  value: `${ex.cap_rate.toFixed(2)}%` });
    if (ex.noi_monthly)       rows.push({ label: 'NOI',       value: `$${ex.noi_monthly.toLocaleString()}/mo` });
    if (ex.cash_flow_monthly) rows.push({ label: 'cash flow', value: `$${ex.cash_flow_monthly.toLocaleString()}/mo` });
  }

  return (
    <CardChrome ctx={ctx}
      title={ex?.property_name || summary}
      eyebrow={`Voice memo · ${recordedLabel}`}
    >
      {v.parse_error && (
        <div style={{
          background: '#FFB3B311',
          border: '1px solid #FFB3B344',
          borderRadius: 8, padding: '6px 10px',
          fontSize: 10, color: '#CC2200', marginBottom: 8,
        }}>
          AI parsing returned: {v.parse_error}
        </div>
      )}

      {/* Summary line */}
      {ex?.summary && (
        <div style={{
          fontSize: 12, color: ctx.fg, lineHeight: 1.55, marginBottom: 10,
          fontStyle: 'italic',
        }}>
          "{ex.summary}"
        </div>
      )}

      {/* Structured rows */}
      {rows.length > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px',
          marginBottom: 10,
        }}>
          {rows.map((r, i) => (
            <div key={i}>
              <div style={{
                fontSize: 8, color: ctx.fgMute, fontWeight: 700,
                letterSpacing: '0.12em', textTransform: 'uppercase',
              }}>{r.label}</div>
              <div style={{
                fontFamily: 'Fraunces, Georgia, serif',
                fontSize: 13, fontWeight: 700, color: ctx.fg,
              }}>{r.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Notes / issues / amenities — single-card view (no promote since
          we don't have a row updater here). User can re-record or promote
          from the voice-table version of this same memo. */}
      {ex?.notes && ex.notes.length > 0 && (
        <ChipGroup ctx={ctx} label="Notes" items={ex.notes} tone="default"
          source="notes" />
      )}
      {ex?.issues && ex.issues.length > 0 && (
        <ChipGroup ctx={ctx} label="Issues" items={ex.issues} tone="warn"
          source="issues" />
      )}
      {ex?.amenities && ex.amenities.length > 0 && (
        <ChipGroup ctx={ctx} label="Amenities" items={ex.amenities} tone="accent"
          source="amenities" />
      )}

      {/* Action row: generate deck + expand transcript */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        marginTop: 8, marginBottom: 4,
      }}>
        <button
          onClick={(e) => { e.stopPropagation(); setDeckOpen(true); }}
          onMouseDown={e => e.stopPropagation()}
          disabled={!ex}
          title={ex
            ? 'Generate a pitch deck from this memo'
            : 'No parsed data — can\'t generate a deck'}
          style={{
            background: ex ? ctx.accent : 'transparent',
            border: `1px solid ${ctx.accent}`,
            color: ex ? '#0a1322' : ctx.fgMute,
            fontSize: 9, fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            padding: '3px 8px', borderRadius: 5,
            cursor: ex ? 'pointer' : 'not-allowed',
            opacity: ex ? 1 : 0.5,
            fontFamily: 'inherit',
          }}>
          Generate deck
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setShowTranscript(s => !s); }}
          onMouseDown={e => e.stopPropagation()}
          style={{
            background: 'transparent', border: 'none', color: ctx.accent,
            fontSize: 9, fontWeight: 700, letterSpacing: '0.14em',
            textTransform: 'uppercase', cursor: 'pointer', padding: 0,
            fontFamily: 'inherit',
          }}>
          {showTranscript ? '▼ Hide transcript' : '▶ Show transcript'}
        </button>
        {showTranscript && onUpdate && !editing && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDraftTranscript(v.transcript || '');
              setReparseError(null);
              setEditing(true);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              background: 'transparent', border: `1px solid ${ctx.border}`,
              color: ctx.fgMute, fontSize: 9, fontWeight: 700,
              letterSpacing: '0.10em', textTransform: 'uppercase',
              padding: '2px 7px', borderRadius: 4, cursor: 'pointer',
              fontFamily: 'inherit',
            }}>
            Edit & re-parse
          </button>
        )}
      </div>
      {deckOpen && (
        <DeckViewer
          memo={{
            recorded_at: v.recorded_at,
            transcript:  v.transcript,
            extracted:   v.extracted as any,
            entities:    v.entities as any,
          }}
          pdfTitle={ex?.property_name || ex?.summary || 'Memo brief'}
          token={ctx.token}
          onClose={() => setDeckOpen(false)}
        />
      )}
      {showTranscript && !editing && (
        <div
          data-editable="true"
          onMouseDown={e => e.stopPropagation()}
          style={{
            fontSize: 11, color: ctx.fg, lineHeight: 1.6,
            background: ctx.theme === 'light' ? '#FAFAF9' : '#1A1A20',
            border: `1px solid ${ctx.border}`,
            borderRadius: 8, padding: 10, marginTop: 4,
            maxHeight: 180, overflowY: 'auto', fontStyle: 'italic',
          }}>
          "{v.transcript}"
        </div>
      )}
      {showTranscript && editing && (
        <div onMouseDown={(e) => e.stopPropagation()}
             style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
          <textarea
            value={draftTranscript}
            onChange={(e) => setDraftTranscript(e.target.value)}
            style={{
              width: '100%', minHeight: 120, maxHeight: 240,
              resize: 'vertical',
              background: ctx.theme === 'light' ? '#FFFFFF' : '#0F0F12',
              color: ctx.fg, border: `1px solid ${ctx.accent}55`,
              borderRadius: 6, padding: 10, fontSize: 11, lineHeight: 1.55,
              fontFamily: 'ui-monospace, SFMono-Regular, monospace',
              outline: 'none',
            }}
          />
          {reparseError && (
            <div style={{ color: '#CC2200', fontSize: 10, fontStyle: 'italic' }}>
              {reparseError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={(e) => { e.stopPropagation(); setEditing(false); setReparseError(null); }}
              onMouseDown={(e) => e.stopPropagation()}
              disabled={reparseBusy}
              style={{
                background: 'transparent', border: `1px solid ${ctx.border}`,
                color: ctx.fgMute, fontSize: 9, fontWeight: 700,
                letterSpacing: '0.10em', textTransform: 'uppercase',
                padding: '4px 10px', borderRadius: 4,
                cursor: reparseBusy ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', opacity: reparseBusy ? 0.5 : 1,
              }}>
              Cancel
            </button>
            <button
              onClick={async (e) => {
                e.stopPropagation();
                const text = draftTranscript.trim();
                if (!text) {
                  setReparseError('Transcript cannot be empty.');
                  return;
                }
                setReparseBusy(true);
                setReparseError(null);
                try {
                  const r = await axios.post(`${API}/me/voice-parse`,
                    { transcript: text },
                    { headers: { Authorization: `Bearer ${ctx.token}` }, timeout: 90_000 });
                  const data = r.data as { transcript: string; extracted: any; entities?: any; parse_error?: string };
                  if (!data.extracted) {
                    setReparseError(data.parse_error || 'Parse returned no data. Edit not saved.');
                    return;
                  }
                  onUpdate?.({
                    voice: {
                      ...v,
                      transcript:  data.transcript,
                      extracted:   data.extracted,
                      entities:    data.entities ?? v.entities,
                      parse_error: undefined,
                    },
                  });
                  setEditing(false);
                } catch (err: any) {
                  const detail: string = err?.response?.data?.detail
                              || err?.response?.data?.message
                              || err?.message
                              || 'Re-parse failed';
                  if (/credit balance is too low|insufficient_credit/i.test(detail)) {
                    setReparseError('Anthropic credits empty. Add credits at console.anthropic.com.');
                  } else {
                    setReparseError(detail);
                  }
                } finally {
                  setReparseBusy(false);
                }
              }}
              onMouseDown={(e) => e.stopPropagation()}
              disabled={reparseBusy}
              style={{
                background: ctx.accent, color: '#0a1322', border: 'none',
                fontSize: 9, fontWeight: 700, letterSpacing: '0.10em',
                textTransform: 'uppercase',
                padding: '4px 12px', borderRadius: 4,
                cursor: reparseBusy ? 'wait' : 'pointer',
                fontFamily: 'inherit', opacity: reparseBusy ? 0.7 : 1,
              }}>
              {reparseBusy ? 'Re-parsing…' : 'Save & re-parse'}
            </button>
          </div>
        </div>
      )}
    </CardChrome>
  );
}

// ── Voice TABLE card: a growing spreadsheet of voice memos ──────────

interface TableCol {
  key:   string;
  label: string;
  width: number;
  align?: 'left' | 'right';
}
const TABLE_COLS: TableCol[] = [
  { key: 'property_name', label: 'Property',    width: 160 },
  { key: 'location',      label: 'Location',    width: 160 },
  { key: 'units',         label: 'Units',       width: 60,  align: 'right' },
  { key: 'cap_rate',      label: 'Cap rate',    width: 70,  align: 'right' },
  { key: 'rent_monthly',  label: 'Rent',        width: 90,  align: 'right' },
  { key: 'cash_flow_monthly', label: 'CF/mo',   width: 80,  align: 'right' },
  { key: 'year_built',    label: 'Built',       width: 60,  align: 'right' },
];

function VoiceTableCard({ card, ctx, onUpdate, onOpenRecorder }: {
  card: BoardCard;
  ctx:  CardCtx;
  onUpdate: (patch: Partial<BoardCard>) => void;
  onOpenRecorder: () => void;
}) {
  const t = card.voiceTable;
  const [expanded, setExpanded] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<string>('recorded_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [editingTitle, setEditingTitle] = useState(false);
  // Cell-edit state: which (rowId, columnKey) is being edited
  const [editingCell, setEditingCell] = useState<{ rowId: string; key: string } | null>(null);
  const [cellDraft, setCellDraft] = useState('');

  if (!t) return <CardChrome ctx={ctx}>No table data.</CardChrome>;

  const startCellEdit = (row: VoiceMemoRow, key: string) => {
    if (!t) return;
    const ex = row.extracted as Record<string, unknown> | null;
    const current = ex?.[key];
    setEditingCell({ rowId: row.id, key });
    setCellDraft(current === null || current === undefined ? '' : String(current));
  };

  const commitCellEdit = () => {
    if (!t || !editingCell) return;
    const { rowId, key } = editingCell;
    // Cast input to the right type (number vs string) based on column
    const numericKeys = new Set([
      'units', 'year_built', 'purchase_price', 'rent_monthly',
      'cap_rate', 'noi_monthly', 'cash_flow_monthly', 'sqft',
    ]);
    let newValue: unknown = cellDraft.trim();
    if (newValue === '') {
      newValue = null;
    } else if (numericKeys.has(key)) {
      const cleaned = String(newValue).replace(/[$,%\s]/g, '');
      const n = Number(cleaned);
      newValue = Number.isFinite(n) ? n : null;
    }
    const nextRows = t.rows.map(r => {
      if (r.id !== rowId) return r;
      const baseExtracted = r.extracted || {
        property_name: null, location: null, address: null,
        property_type: null, units: null, year_built: null,
        purchase_price: null, rent_monthly: null, cap_rate: null,
        noi_monthly: null, cash_flow_monthly: null, sqft: null,
        amenities: [], issues: [], notes: [], summary: '',
      };
      return {
        ...r,
        extracted: { ...baseExtracted, [key]: newValue },
      } as VoiceMemoRow;
    });
    onUpdate({ voiceTable: { ...t, rows: nextRows } });
    setEditingCell(null);
    setCellDraft('');
  };

  const cancelCellEdit = () => {
    setEditingCell(null);
    setCellDraft('');
  };

  const fmt = (key: string, val: unknown): string => {
    if (val === null || val === undefined) return '—';
    if (key === 'rent_monthly' || key === 'cash_flow_monthly'
       || key === 'noi_monthly' || key === 'purchase_price') {
      return typeof val === 'number' ? `$${val.toLocaleString()}` : String(val);
    }
    if (key === 'cap_rate') {
      return typeof val === 'number' ? `${val.toFixed(2)}%` : String(val);
    }
    return String(val);
  };

  const q = query.trim().toLowerCase();
  const rowsAll = t.rows.filter(r => {
    if (!q) return true;
    const hay = JSON.stringify(r.extracted) + ' ' + r.transcript;
    return hay.toLowerCase().includes(q);
  });
  const rows = [...rowsAll].sort((a, b) => {
    if (sortKey === 'recorded_at') {
      const ai = a.recorded_at || '', bi = b.recorded_at || '';
      return sortDir === 'asc' ? ai.localeCompare(bi) : bi.localeCompare(ai);
    }
    const aval = a.extracted?.[sortKey as keyof typeof a.extracted];
    const bval = b.extracted?.[sortKey as keyof typeof b.extracted];
    if (aval === null || aval === undefined) return 1;
    if (bval === null || bval === undefined) return -1;
    if (typeof aval === 'number' && typeof bval === 'number') {
      return sortDir === 'asc' ? aval - bval : bval - aval;
    }
    const as = String(aval), bs = String(bval);
    return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
  });

  const toggleSort = (k: string) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir(k === 'recorded_at' ? 'desc' : 'asc'); }
  };

  const deleteRow = (id: string) => {
    onUpdate({ voiceTable: { ...t, rows: t.rows.filter(r => r.id !== id) } });
    if (expanded === id) setExpanded(null);
  };

  return (
    <div style={{
      width: '100%', height: '100%', boxSizing: 'border-box',
      padding: '12px 14px 14px', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Title row */}
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8,
      }}>
        {editingTitle ? (
          <input
            autoFocus
            value={t.title}
            onChange={e => onUpdate({ voiceTable: { ...t, title: e.target.value } })}
            onBlur={() => setEditingTitle(false)}
            onKeyDown={e => { if (e.key === 'Enter') setEditingTitle(false); }}
            onMouseDown={e => e.stopPropagation()}
            style={{
              flex: 1, fontFamily: 'Fraunces, Georgia, serif',
              fontSize: 18, fontWeight: 800, color: ctx.fg,
              background: 'transparent', border: 'none', outline: 'none',
            }} />
        ) : (
          <div onClick={(e) => { e.stopPropagation(); setEditingTitle(true); }}
            style={{
              flex: 1, fontFamily: 'Fraunces, Georgia, serif',
              fontSize: 18, fontWeight: 800, color: ctx.fg,
              cursor: 'text',
            }}>
            {t.title}
          </div>
        )}
        <span style={{
          color: ctx.fgMute, fontSize: 10, fontWeight: 600,
          letterSpacing: '0.06em',
        }}>
          {t.rows.length} {t.rows.length === 1 ? 'memo' : 'memos'}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onOpenRecorder(); }}
          onMouseDown={e => e.stopPropagation()}
          style={{
            background: ctx.accent, color: '#fff',
            border: `1px solid ${ctx.accent}`, borderRadius: 8,
            fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', padding: '5px 10px',
            cursor: 'pointer', fontFamily: 'inherit',
          }}>
          + Record
        </button>
      </div>

      {/* Search */}
      <input
        value={query}
        onChange={e => setQuery(e.target.value)}
        onMouseDown={e => e.stopPropagation()}
        placeholder="Search memos by name, location, notes…"
        style={{
          background: ctx.theme === 'light' ? '#FAFAF9' : '#0F0F12',
          color: ctx.fg, border: `1px solid ${ctx.border}`,
          borderRadius: 8, padding: '6px 10px',
          fontSize: 11, marginBottom: 4, fontFamily: 'inherit',
          outline: 'none',
        }} />

      <div style={{
        fontSize: 9, color: ctx.fgMute, marginBottom: 6,
        letterSpacing: '0.04em',
      }}>
        Double-click any cell to fill in or correct what AI missed
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {rows.length === 0 ? (
          <div style={{
            color: ctx.fgMute, fontSize: 11, fontStyle: 'italic',
            padding: '24px 0', textAlign: 'center',
            border: `1px dashed ${ctx.border}`, borderRadius: 8,
          }}>
            {q ? 'No memos match this search.' : 'No memos yet. Click + Record to add one.'}
          </div>
        ) : (
          <table style={{
            width: '100%', borderCollapse: 'collapse',
            fontSize: 11, color: ctx.fg,
          }}>
            <thead>
              <tr style={{
                background: ctx.theme === 'light' ? '#FAFAF9' : '#0F0F12',
              }}>
                {TABLE_COLS.map(c => (
                  <th key={c.key}
                    onClick={(e) => { e.stopPropagation(); toggleSort(c.key); }}
                    onMouseDown={e => e.stopPropagation()}
                    style={{
                      padding: '6px 8px',
                      textAlign: c.align || 'left',
                      color: ctx.fgMute,
                      fontWeight: 700, fontSize: 9,
                      letterSpacing: '0.1em', textTransform: 'uppercase',
                      borderBottom: `1px solid ${ctx.border}`,
                      cursor: 'pointer', whiteSpace: 'nowrap',
                      width: c.width,
                    }}>
                    {c.label}
                    {sortKey === c.key && (
                      <span style={{ marginLeft: 4, color: ctx.accent }}>
                        {sortDir === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                ))}
                <th style={{
                  padding: '6px 4px', borderBottom: `1px solid ${ctx.border}`,
                  width: 22,
                }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isExpanded = expanded === row.id;
                const ex = row.extracted;
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      onClick={(e) => { e.stopPropagation(); setExpanded(isExpanded ? null : row.id); }}
                      onMouseDown={e => e.stopPropagation()}
                      style={{
                        cursor: 'pointer',
                        background: isExpanded
                          ? (ctx.theme === 'light' ? '#F5F0E8' : '#252530')
                          : 'transparent',
                        transition: 'background 0.12s',
                      }}>
                      {TABLE_COLS.map(c => {
                        const isEditing = editingCell?.rowId === row.id
                                       && editingCell?.key === c.key;
                        const cellValue = ex?.[c.key as keyof typeof ex];
                        const isEmpty = cellValue === null || cellValue === undefined;
                        return (
                          <td key={c.key}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              startCellEdit(row, c.key);
                            }}
                            title="Double-click to edit"
                            style={{
                              padding: '6px 8px',
                              textAlign: c.align || 'left',
                              borderBottom: `1px solid ${ctx.border}55`,
                              fontFamily: c.key === 'property_name' || c.key === 'location'
                                ? 'inherit' : 'Fraunces, Georgia, serif',
                              fontWeight: c.key === 'property_name' ? 600 : 500,
                              color: isEmpty ? ctx.fgMute : ctx.fg,
                              overflow: 'hidden', textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap', maxWidth: c.width,
                              cursor: 'text',
                            }}>
                            {isEditing ? (
                              <input
                                autoFocus
                                value={cellDraft}
                                onChange={e => setCellDraft(e.target.value)}
                                onMouseDown={e => e.stopPropagation()}
                                onClick={e => e.stopPropagation()}
                                onBlur={commitCellEdit}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') commitCellEdit();
                                  if (e.key === 'Escape') cancelCellEdit();
                                }}
                                style={{
                                  width: '100%',
                                  background: ctx.theme === 'light' ? '#fff' : '#0F0F12',
                                  border: `1px solid ${ctx.accent}`,
                                  borderRadius: 4,
                                  color: ctx.fg, fontSize: 11,
                                  padding: '2px 6px',
                                  fontFamily: c.key === 'property_name' || c.key === 'location'
                                    ? 'inherit' : 'Fraunces, Georgia, serif',
                                  textAlign: c.align || 'left',
                                  outline: 'none',
                                }} />
                            ) : (
                              ex
                                ? fmt(c.key, cellValue)
                                : (c.key === 'property_name' ? '(unparsed)' : '—')
                            )}
                          </td>
                        );
                      })}
                      <td style={{
                        padding: '4px',
                        textAlign: 'center',
                        borderBottom: `1px solid ${ctx.border}55`,
                      }}>
                        <span style={{
                          color: isExpanded ? ctx.accent : ctx.fgMute, fontSize: 10,
                          display: 'inline-block',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.15s',
                        }}>▶</span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={TABLE_COLS.length + 1} style={{
                          padding: '10px 14px',
                          background: ctx.theme === 'light' ? '#FAFAF9' : '#1A1A20',
                          borderBottom: `1px solid ${ctx.border}`,
                        }}>
                          <VoiceMemoDetail row={row} ctx={ctx}
                            onDelete={() => deleteRow(row.id)}
                            onUpdateRow={(next) => {
                              if (!t) return;
                              onUpdate({
                                voiceTable: {
                                  ...t,
                                  rows: t.rows.map(r => r.id === row.id ? next : r),
                                },
                              });
                            }} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function VoiceMemoDetail({ row, ctx, onDelete, onUpdateRow }: {
  row: VoiceMemoRow; ctx: CardCtx;
  onDelete: () => void;
  onUpdateRow: (next: VoiceMemoRow) => void;
}) {
  // Move a chip from one source array to a structured field, another array,
  // or delete it. All operations are pure functions over row.extracted.
  const promote = (source: 'notes' | 'issues' | 'amenities') =>
    (index: number, target: PromoteTarget) => {
      if (!row.extracted) return;
      const ex = { ...row.extracted };
      // Make sure arrays exist (defensive)
      const notes = [...(ex.notes || [])];
      const issues = [...(ex.issues || [])];
      const amenities = [...(ex.amenities || [])];
      const sourceArr = source === 'notes' ? notes
                      : source === 'issues' ? issues
                      : amenities;
      const value = sourceArr[index];
      if (value === undefined) return;
      // Remove from source
      sourceArr.splice(index, 1);
      ex.notes = notes;
      ex.issues = issues;
      ex.amenities = amenities;
      // Place into target
      if (target.kind === 'field') {
        (ex as Record<string, unknown>)[target.field] = String(value);
      } else if (target.kind === 'numeric') {
        const cleaned = String(value).replace(/[$,%\s]/g, '');
        // Try to find a number anywhere in the value (e.g. "around 7%")
        const m = cleaned.match(/-?\d+(?:\.\d+)?/);
        const n = m ? Number(m[0]) : NaN;
        (ex as Record<string, unknown>)[target.field] =
          Number.isFinite(n) ? n : null;
      } else if (target.kind === 'array') {
        const targetArr = target.field === 'notes' ? notes
                        : target.field === 'issues' ? issues
                        : amenities;
        targetArr.push(value);
      }
      // 'delete' just removes — already done above
      onUpdateRow({ ...row, extracted: ex });
    };

  const [showTranscript, setShowTranscript] = useState(false);
  const ex = row.extracted;
  const recordedAt = new Date(row.recorded_at).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 12,
      }}>
        <div style={{
          color: ctx.fgMute, fontSize: 9, fontWeight: 600,
          letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>
          Recorded {recordedAt}
        </div>
        <span style={{ flex: 1 }} />
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          onMouseDown={e => e.stopPropagation()}
          style={{
            background: 'transparent', border: `1px solid ${ctx.border}`,
            color: '#CC2200', fontSize: 9, fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            padding: '3px 8px', borderRadius: 5, cursor: 'pointer',
            fontFamily: 'inherit',
          }}>
            Delete row
          </button>
      </div>

      {ex?.summary && (
        <div style={{
          fontSize: 12, color: ctx.fg, lineHeight: 1.5,
          fontStyle: 'italic',
        }}>
          "{ex.summary}"
        </div>
      )}

      {/* Extra structured fields not shown in the table row */}
      {ex && (ex.address || ex.property_type || ex.sqft || ex.noi_monthly || ex.purchase_price) && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '4px 12px',
        }}>
          {ex.address && <DetailKV ctx={ctx} k="address" v={ex.address} />}
          {ex.property_type && <DetailKV ctx={ctx} k="type" v={ex.property_type} />}
          {ex.sqft && <DetailKV ctx={ctx} k="sqft" v={`${ex.sqft.toLocaleString()} sqft`} />}
          {ex.purchase_price && <DetailKV ctx={ctx} k="price" v={`$${ex.purchase_price.toLocaleString()}`} />}
          {ex.noi_monthly && <DetailKV ctx={ctx} k="NOI" v={`$${ex.noi_monthly.toLocaleString()}/mo`} />}
        </div>
      )}

      {ex?.notes && ex.notes.length > 0 && (
        <ChipGroup ctx={ctx} label="Notes" items={ex.notes} tone="default"
          source="notes" onPromote={promote('notes')} />
      )}
      {ex?.issues && ex.issues.length > 0 && (
        <ChipGroup ctx={ctx} label="Issues" items={ex.issues} tone="warn"
          source="issues" onPromote={promote('issues')} />
      )}
      {ex?.amenities && ex.amenities.length > 0 && (
        <ChipGroup ctx={ctx} label="Amenities" items={ex.amenities} tone="accent"
          source="amenities" onPromote={promote('amenities')} />
      )}

      {/* Verbatim-only entity extraction — bridges Board → Map.
          Address chips are clickable and deeplink to /map?intel_address=...
          so the user can pull intel for any address mentioned in the memo
          with a single click.
          row.entities is populated in-band by /me/voice-parse (combined
          Claude call). When present, EntityChips skips its fetch entirely
          — the chips appear instantly without a second round-trip. */}
      <EntityChips text={row.transcript} cacheKey={`memo:${row.id}`}
                   prefetched={row.entities ?? null} ctx={ctx} />

      {row.parse_error && (
        <div style={{
          background: '#CC220011', border: '1px solid #CC220044',
          borderRadius: 6, padding: '6px 10px',
          fontSize: 10, color: '#CC2200',
        }}>
          Parse note: {row.parse_error}
        </div>
      )}

      <button
        onClick={(e) => { e.stopPropagation(); setShowTranscript(s => !s); }}
        onMouseDown={e => e.stopPropagation()}
        style={{
          background: 'transparent', border: 'none', color: ctx.accent,
          fontSize: 9, fontWeight: 700, letterSpacing: '0.12em',
          textTransform: 'uppercase', cursor: 'pointer', padding: 0,
          textAlign: 'left', fontFamily: 'inherit',
        }}>
        {showTranscript ? '▼ Hide transcript' : '▶ Show transcript'}
      </button>
      {showTranscript && (
        <div
          data-editable="true"
          onMouseDown={e => e.stopPropagation()}
          style={{
            fontSize: 11, color: ctx.fg, lineHeight: 1.55,
            background: ctx.theme === 'light' ? '#FFFFFF' : '#0F0F12',
            border: `1px solid ${ctx.border}`,
            borderRadius: 6, padding: 10, fontStyle: 'italic',
            maxHeight: 200, overflowY: 'auto',
          }}>
          "{row.transcript}"
        </div>
      )}
    </div>
  );
}

/* ── EntityChips ───────────────────────────────────────────────────────
 *  Calls /me/extract-entities for the given text and renders chips
 *  grouped by category. Strict verbatim-only by construction — both the
 *  prompt and the backend post-filter enforce that every chip is a
 *  literal substring of `text`. No fabrication.
 *
 *  Address chips deeplink to /map?intel_address=..., which CityMap
 *  reads on mount and auto-opens the intel panel with that address
 *  pre-searched. This is the Board → Map handoff.
 *
 *  Caching: results are stashed in the module-level `_cache` (which
 *  Board already uses for live-card data) keyed by `cacheKey`. A
 *  dragged or resized card doesn't refetch. Edit the transcript and
 *  the cacheKey should change at the call site to force a refresh
 *  (currently it's `memo:{row.id}`, so editing transcript without
 *  changing id keeps the cached chips — acceptable for v1 since memos
 *  are immutable once parsed).
 */
interface ExtractedEntities {
  addresses:   string[];
  properties:  string[];
  tribes:      string[];
  regulations: string[];
  people:      string[];
  money:       string[];
  parse_error?: string;
}

function EntityChips({ text, cacheKey, prefetched, ctx }: {
  text: string;
  cacheKey: string;
  // When provided, EntityChips skips the network call entirely. This is
  // the fast path — /me/voice-parse already extracted entities in the
  // same Claude round-trip, so we just render them.
  prefetched?: ExtractedEntities | null;
  ctx: CardCtx;
}) {
  // Seed state from (in priority order):
  //   1. prefetched prop  (memos parsed by combined voice-parse)
  //   2. module cache     (already-fetched older memos)
  //   3. null             (cold — will fetch)
  const [data, setData] = useState<ExtractedEntities | null>(() => {
    if (prefetched) return prefetched;
    if (cacheKey && _cache[`ent:${cacheKey}`]) {
      return _cache[`ent:${cacheKey}`] as ExtractedEntities;
    }
    return null;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Prefetched path: nothing to do, the chips already have data.
    if (prefetched) {
      setData(prefetched);
      // Also stash in the cache so a remount picks it up instantly.
      if (cacheKey) _cache[`ent:${cacheKey}`] = prefetched;
      return;
    }
    if (!text || text.trim().length < 20) { setData(null); return; }
    if (_cache[`ent:${cacheKey}`]) {
      setData(_cache[`ent:${cacheKey}`] as ExtractedEntities);
      return;
    }
    let alive = true;
    setLoading(true);
    // Fallback only — older memos saved before /me/voice-parse returned
    // entities in-band. New memos never hit this branch.
    axios.post(`${API}/me/extract-entities`,
      { text },
      { headers: { Authorization: `Bearer ${ctx.token}` }, timeout: 30000 },
    ).then(r => {
      if (!alive) return;
      const d = r.data as ExtractedEntities;
      _cache[`ent:${cacheKey}`] = d;
      setData(d);
    }).catch(() => {
      // Soft-fail — entity extraction is supplementary, not blocking.
      if (alive) setData(null);
    }).finally(() => {
      if (alive) setLoading(false);
    });
    return () => { alive = false; };
  }, [text, cacheKey, prefetched, ctx.token]);

  if (loading && !data) {
    return (
      <div style={{
        fontSize: 9, color: ctx.fgMute, fontStyle: 'italic',
        letterSpacing: '0.08em', textTransform: 'uppercase',
      }}>
        Reading entities…
      </div>
    );
  }
  if (!data) return null;
  const totalCount = data.addresses.length + data.properties.length
                   + data.tribes.length + data.regulations.length
                   + data.people.length + data.money.length;
  if (totalCount === 0) return null;

  const openOnMap = (address: string) => {
    const url = `/map?intel_address=${encodeURIComponent(address)}`;
    window.history.pushState({}, '', url);
    window.dispatchEvent(new Event('popstate'));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{
        fontSize: 8, color: ctx.fgMute, fontWeight: 700,
        letterSpacing: '0.14em', textTransform: 'uppercase',
      }}>
        Mentioned in this memo
      </div>

      {/* Address chips — clickable, deeplink to Map intel for that address */}
      {data.addresses.length > 0 && (
        <EntityRow ctx={ctx} label="Address" items={data.addresses}
          actionable
          onClick={openOnMap}
          actionLabel="Test on Map →" />
      )}
      {data.properties.length > 0 && (
        <EntityRow ctx={ctx} label="Property" items={data.properties} />
      )}
      {data.tribes.length > 0 && (
        <EntityRow ctx={ctx} label="Tribe" items={data.tribes} />
      )}
      {data.regulations.length > 0 && (
        <EntityRow ctx={ctx} label="Reg / case" items={data.regulations} />
      )}
      {data.people.length > 0 && (
        <EntityRow ctx={ctx} label="Person" items={data.people} />
      )}
      {data.money.length > 0 && (
        <EntityRow ctx={ctx} label="$" items={data.money} />
      )}
    </div>
  );
}

function EntityRow({ ctx, label, items, actionable, onClick, actionLabel }: {
  ctx: CardCtx;
  label: string;
  items: string[];
  actionable?: boolean;
  onClick?: (item: string) => void;
  actionLabel?: string;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap',
    }}>
      <div style={{
        flexShrink: 0, fontSize: 8, color: ctx.fgMute,
        fontWeight: 700, letterSpacing: '0.10em',
        textTransform: 'uppercase', minWidth: 60,
        paddingTop: 3,
      }}>
        {label}
      </div>
      <div style={{
        flex: 1, display: 'flex', flexWrap: 'wrap', gap: 4,
      }}>
        {items.map((it, i) => (
          <button
            key={`${label}-${i}`}
            onClick={(e) => {
              e.stopPropagation();
              if (actionable && onClick) onClick(it);
            }}
            onMouseDown={e => e.stopPropagation()}
            disabled={!actionable}
            style={{
              fontSize: 10,
              padding: '2px 8px',
              borderRadius: 999,
              fontFamily: 'inherit',
              border: `1px solid ${actionable ? ctx.accent : ctx.border}`,
              background: actionable
                ? (ctx.theme === 'light' ? 'rgba(201,169,110,0.10)' : 'rgba(201,169,110,0.15)')
                : 'transparent',
              color: actionable ? ctx.accent : ctx.fg,
              cursor: actionable ? 'pointer' : 'default',
              transition: 'background .15s',
            }}
            title={actionable && actionLabel ? actionLabel : undefined}
          >
            {it}{actionable ? ' →' : ''}
          </button>
        ))}
      </div>
    </div>
  );
}


function DetailKV({ ctx, k, v }: { ctx: CardCtx; k: string; v: string }) {
  return (
    <div>
      <div style={{
        fontSize: 8, color: ctx.fgMute, fontWeight: 700,
        letterSpacing: '0.1em', textTransform: 'uppercase',
      }}>{k}</div>
      <div style={{
        fontFamily: 'Fraunces, Georgia, serif',
        fontSize: 11, fontWeight: 600, color: ctx.fg,
      }}>{v}</div>
    </div>
  );
}

// Targets the user can promote a note/issue/amenity into.
type PromoteTarget =
  | { kind: 'field';  field: 'property_name' | 'location' | 'address' | 'property_type' }
  | { kind: 'numeric'; field: 'units' | 'year_built' | 'purchase_price' | 'rent_monthly'
                              | 'cap_rate' | 'noi_monthly' | 'cash_flow_monthly' | 'sqft' }
  | { kind: 'array';  field: 'amenities' | 'issues' | 'notes' }
  | { kind: 'delete' };

interface ChipGroupProps {
  ctx: CardCtx;
  label: string;
  items: string[];
  tone: 'default' | 'warn' | 'accent';
  source: 'notes' | 'issues' | 'amenities';  // where these chips live
  onPromote?: (index: number, target: PromoteTarget) => void;
}

function ChipGroup({ ctx, label, items, tone, source, onPromote }: ChipGroupProps) {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const toneColor = tone === 'warn' ? '#CC2200'
                  : tone === 'accent' ? ctx.accent
                  : ctx.fg;
  const toneBg = tone === 'warn' ? '#CC220011'
               : tone === 'accent' ? ctx.accent + '15'
               : ctx.theme === 'light' ? '#F5F0E8' : '#252530';

  // Field options shown in the popover, ordered for typical use
  const fieldOpts: Array<{ label: string; target: PromoteTarget }> = [
    { label: 'Property name', target: { kind: 'field',  field: 'property_name' } },
    { label: 'Location',      target: { kind: 'field',  field: 'location'      } },
    { label: 'Address',       target: { kind: 'field',  field: 'address'       } },
    { label: 'Property type', target: { kind: 'field',  field: 'property_type' } },
    { label: 'Units',         target: { kind: 'numeric', field: 'units'        } },
    { label: 'Year built',    target: { kind: 'numeric', field: 'year_built'   } },
    { label: 'Sqft',          target: { kind: 'numeric', field: 'sqft'         } },
    { label: 'Rent/mo',       target: { kind: 'numeric', field: 'rent_monthly' } },
    { label: 'Cap rate',      target: { kind: 'numeric', field: 'cap_rate'     } },
    { label: 'NOI/mo',        target: { kind: 'numeric', field: 'noi_monthly'  } },
    { label: 'Cash flow/mo',  target: { kind: 'numeric', field: 'cash_flow_monthly' } },
    { label: 'Price',         target: { kind: 'numeric', field: 'purchase_price' } },
    // Cross-list moves
    ...(source !== 'amenities' ? [{ label: 'Move to Amenities', target: { kind: 'array' as const, field: 'amenities' as const } }] : []),
    ...(source !== 'issues'    ? [{ label: 'Move to Issues',    target: { kind: 'array' as const, field: 'issues' as const    } }] : []),
    ...(source !== 'notes'     ? [{ label: 'Move to Notes',     target: { kind: 'array' as const, field: 'notes' as const     } }] : []),
  ];

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{
        color: ctx.fgMute, fontSize: 8, fontWeight: 700,
        letterSpacing: '0.12em', textTransform: 'uppercase',
        marginBottom: 3,
      }}>{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {items.map((it, i) => (
          <span key={i} style={{ position: 'relative' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onPromote) setOpenIdx(openIdx === i ? null : i);
              }}
              onMouseDown={e => e.stopPropagation()}
              title={onPromote ? "Click → move this to the right field" : undefined}
              style={{
                fontSize: 10, color: toneColor, fontWeight: 500,
                background: toneBg, border: `1px solid ${toneColor}33`,
                padding: '2px 8px', borderRadius: 10,
                cursor: onPromote ? 'pointer' : 'default',
                fontFamily: 'inherit',
              }}>
              {it}
            </button>
            {openIdx === i && onPromote && (
              <div
                onMouseDown={e => e.stopPropagation()}
                style={{
                  position: 'absolute', top: '110%', left: 0, zIndex: 100,
                  background: ctx.theme === 'light' ? '#FFFFFF' : '#1A1A20',
                  color: ctx.fg,
                  border: `1px solid ${ctx.border}`,
                  borderRadius: 10, padding: 6,
                  boxShadow: '0 8px 28px rgba(0,0,0,0.2)',
                  minWidth: 180,
                  display: 'flex', flexDirection: 'column', gap: 1,
                  maxHeight: 280, overflowY: 'auto',
                }}>
                <div style={{
                  fontSize: 8, color: ctx.fgMute, fontWeight: 700,
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  padding: '4px 8px',
                }}>Move "{it.length > 24 ? it.slice(0, 22) + '…' : it}" to</div>
                {fieldOpts.map((opt, j) => (
                  <button key={j}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPromote(i, opt.target);
                      setOpenIdx(null);
                    }}
                    style={{
                      background: 'transparent', border: 'none',
                      color: ctx.fg, fontSize: 11,
                      padding: '5px 10px', cursor: 'pointer',
                      textAlign: 'left', borderRadius: 5,
                      fontFamily: 'inherit',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = ctx.theme === 'light' ? '#F5F0E8' : '#252530'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    {opt.label}
                  </button>
                ))}
                <div style={{ height: 1, background: ctx.border, margin: '4px 6px' }} />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onPromote(i, { kind: 'delete' });
                    setOpenIdx(null);
                  }}
                  style={{
                    background: 'transparent', border: 'none',
                    color: '#CC2200', fontSize: 11, fontWeight: 600,
                    padding: '5px 10px', cursor: 'pointer',
                    textAlign: 'left', borderRadius: 5,
                    fontFamily: 'inherit',
                  }}>
                  Delete
                </button>
              </div>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Section insight card — fetches a relevant snippet per section ─────

function SectionInsightCard({ sectionId, ctx }: {
  sectionId: string;
  ctx: CardCtx;
}) {
  const sec = SECTIONS.find(s => s.id === sectionId);
  // Markets → top focused MSA
  const market = useCachedFetch<{ markets: Array<{
    name: string; state: string;
    metrics: Record<string, { value: number; year: number; yoy_pct: number | null } | null>;
  }>; }>(
    sectionId === 'markets' ? 'me-markets' : '',
    () => axios.get(`${API}/me/markets`, {
      headers: { Authorization: `Bearer ${ctx.token}` },
    }).then(r => r.data),
  );
  // Peers / Properties → comparison
  const cmp = useCachedFetch<{
    niches: RichNiche[];
    rankings: Record<string, Array<{ name: string; primary_display: string }>>;
  }>(
    (sectionId === 'peers' || sectionId === 'properties') ? 'sheets-comparison-2' : '',
    () => axios.get(`${API}/sheets/2/comparison`, {
      headers: { Authorization: `Bearer ${ctx.token}` },
    }).then(r => r.data),
  );
  // Curriculum / Glossary
  const curriculum = useCachedFetch<{ done: number; total: number; progress: number }>(
    sectionId === 'reading' ? 'me-curriculum' : '',
    () => axios.get(`${API}/me/curriculum`, {
      headers: { Authorization: `Bearer ${ctx.token}` },
    }).then(r => r.data),
  );
  const glossary = useCachedFetch<{ glossary: Array<{ term: string }> }>(
    sectionId === 'glossary' ? 'me-glossary' : '',
    () => axios.get(`${API}/me/glossary`, {
      headers: { Authorization: `Bearer ${ctx.token}` },
    }).then(r => r.data),
  );

  return (
    <div style={{
      width: '100%', height: '100%', boxSizing: 'border-box',
      padding: 14, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        color: ctx.accent, fontSize: 9, fontWeight: 700,
        letterSpacing: '0.18em', textTransform: 'uppercase',
        marginBottom: 4,
      }}>
        Coast · {sec?.label || sectionId}
      </div>
      <div style={{
        fontFamily: 'Fraunces, Georgia, serif',
        fontSize: 16, fontWeight: 700, marginBottom: 8, color: ctx.fg,
      }}>
        {sec?.label || sectionId}
      </div>

      {/* Section-specific insight body */}
      <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {sectionId === 'markets' && (() => {
          if (market.loading) return <SmallMuted ctx={ctx}>Loading…</SmallMuted>;
          const m = market.data?.markets?.[0];
          if (!m) return <SmallMuted ctx={ctx}>{sec?.desc}</SmallMuted>;
          const rent = m.metrics?.['zori_rent_index_mfr'];
          const unemp = m.metrics?.['unemployment_rate_bls'];
          return (
            <div style={{ fontSize: 11, color: ctx.fg, lineHeight: 1.55 }}>
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: ctx.fgMute }}>Top MSA: </span>
                <strong>{m.name}, {m.state}</strong>
              </div>
              {rent && (
                <div style={{ marginBottom: 4 }}>
                  <span style={{ color: ctx.fgMute }}>MF rent (ZORI): </span>
                  <strong>${Math.round(rent.value).toLocaleString()}/mo</strong>
                  {rent.yoy_pct !== null && (
                    <span style={{
                      color: rent.yoy_pct > 0 ? '#1A7A4A' : '#CC2200',
                      marginLeft: 6, fontWeight: 600,
                    }}>{rent.yoy_pct > 0 ? '+' : ''}{rent.yoy_pct.toFixed(1)}% YoY</span>
                  )}
                </div>
              )}
              {unemp && (
                <div>
                  <span style={{ color: ctx.fgMute }}>Unemployment: </span>
                  <strong>{unemp.value.toFixed(1)}%</strong>
                </div>
              )}
            </div>
          );
        })()}

        {sectionId === 'peers' && (() => {
          if (cmp.loading) return <SmallMuted ctx={ctx}>Loading…</SmallMuted>;
          const top = cmp.data?.rankings?.value?.[0];
          if (!top) return <SmallMuted ctx={ctx}>{sec?.desc}</SmallMuted>;
          return (
            <div style={{ fontSize: 11, color: ctx.fg, lineHeight: 1.55 }}>
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: ctx.fgMute }}>Best value: </span>
                <strong>{top.name}</strong>
              </div>
              <div style={{ color: ctx.fgMute }}>
                {top.primary_display} · 5 personas, ranked at /peers
              </div>
            </div>
          );
        })()}

        {sectionId === 'properties' && (() => {
          if (cmp.loading) return <SmallMuted ctx={ctx}>Loading…</SmallMuted>;
          const niches = cmp.data?.niches || [];
          if (niches.length === 0) return <SmallMuted ctx={ctx}>{sec?.desc}</SmallMuted>;
          const cheapest = [...niches].filter(n => n.price !== null)
            .sort((a, b) => (a.price || 0) - (b.price || 0))[0];
          const newest = [...niches].filter(n => n.newness !== null)
            .sort((a, b) => (b.newness || 0) - (a.newness || 0))[0];
          return (
            <div style={{ fontSize: 11, color: ctx.fg, lineHeight: 1.55 }}>
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: ctx.fgMute }}>Buildings tracked: </span>
                <strong>{niches.length}</strong>
              </div>
              {cheapest && (
                <div style={{ marginBottom: 3 }}>
                  <span style={{ color: ctx.fgMute }}>Cheapest: </span>
                  <strong>{cheapest.name}</strong>
                  {cheapest.price && (
                    <span style={{ color: ctx.accent, marginLeft: 4 }}>
                      ${Math.round(cheapest.price).toLocaleString()}/mo
                    </span>
                  )}
                </div>
              )}
              {newest && newest.newness && (
                <div>
                  <span style={{ color: ctx.fgMute }}>Newest: </span>
                  <strong>{newest.name}</strong>
                  <span style={{ color: ctx.accent, marginLeft: 4 }}>
                    {Math.round(newest.newness)}
                  </span>
                </div>
              )}
            </div>
          );
        })()}

        {sectionId === 'searcher' && (
          <div style={{ fontSize: 11, color: ctx.fg, lineHeight: 1.55 }}>
            <div style={{ color: ctx.fgMute, marginBottom: 4 }}>
              Click any US state → live CRE legislative + tax data, plus
              ACS / BLS / FRED indicators for that area.
            </div>
            <div style={{ fontSize: 10, color: ctx.fgMute, marginTop: 6 }}>
              48 indicators · 9 primary sources
            </div>
          </div>
        )}

        {sectionId === 'reading' && (() => {
          if (curriculum.loading) return <SmallMuted ctx={ctx}>Loading…</SmallMuted>;
          const d = curriculum.data;
          if (!d) return <SmallMuted ctx={ctx}>{sec?.desc}</SmallMuted>;
          return (
            <div style={{ fontSize: 11, color: ctx.fg, lineHeight: 1.55 }}>
              <div style={{ marginBottom: 6 }}>
                <strong>{d.done}</strong>
                <span style={{ color: ctx.fgMute }}> of {d.total} read</span>
              </div>
              <div style={{
                height: 6, background: ctx.theme === 'light' ? '#F5F0E8' : '#252530',
                borderRadius: 3, position: 'relative', overflow: 'hidden',
                marginBottom: 4,
              }}>
                <div style={{
                  position: 'absolute', inset: 0,
                  width: `${(d.progress || 0) * 100}%`,
                  background: ctx.accent, borderRadius: 3,
                  transition: 'width 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                }} />
              </div>
              <div style={{ color: ctx.fgMute, fontSize: 10 }}>
                10-item tribal-investment curriculum
              </div>
            </div>
          );
        })()}

        {sectionId === 'glossary' && (() => {
          if (glossary.loading) return <SmallMuted ctx={ctx}>Loading…</SmallMuted>;
          const terms = glossary.data?.glossary || [];
          return (
            <div style={{ fontSize: 11, color: ctx.fg, lineHeight: 1.55 }}>
              <div style={{ marginBottom: 4 }}>
                <strong>{terms.length}</strong>
                <span style={{ color: ctx.fgMute }}> multifamily terms</span>
              </div>
              <div style={{ color: ctx.fgMute, fontSize: 10 }}>
                Sample: {terms.slice(0, 3).map(t => t.term).join(' · ')}…
              </div>
            </div>
          );
        })()}
      </div>

      <div style={{
        fontSize: 9, color: ctx.fgMute, marginTop: 6,
        paddingTop: 6, borderTop: `1px solid ${ctx.border}`,
        letterSpacing: '0.06em', display: 'flex', justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span>Double-click → open in dashboard</span>
        <span style={{ color: ctx.accent, fontWeight: 700 }}>↗</span>
      </div>
    </div>
  );
}

function SmallMuted({ children, ctx }: { children: React.ReactNode; ctx: CardCtx }) {
  return (
    <div style={{
      fontSize: 11, color: ctx.fgMute, lineHeight: 1.5,
    }}>{children}</div>
  );
}

function Stat({ label, value, ctx }: { label: string; value: string; ctx: CardCtx }) {
  return (
    <div>
      <div style={{
        fontSize: 9, color: ctx.fgMute, fontWeight: 600,
        letterSpacing: '0.08em', textTransform: 'uppercase',
      }}>{label}</div>
      <div style={{
        fontFamily: 'Fraunces, Georgia, serif', fontSize: 13, fontWeight: 700,
        color: ctx.fg,
      }}>{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  CHARTS — inline SVG, no external deps. All animate-in via CSS.
// ═══════════════════════════════════════════════════════════════════════

// Enriched niche payload returned from /sheets/{id}/comparison
interface RichNiche {
  name:        string;
  row_index:   number;
  price:       number | null;
  price_unit:  string | null;
  value:       number | null;
  income_min:  number | null;
  newness:     number | null;
  amenity:     number | null;
  location:    string | null;
  rents:       { studio: number | null; '1b1b': number | null; '2b2b': number | null } | null;
  sqfts:       { studio: number | null; '1b1b': number | null; '2b2b': number | null } | null;
  bld_sample:  string[];
  unit_sample: string[];
  bld_all?:    string[];
  unit_all?:   string[];
  leading:     Array<{ label: string; detail: string }>;
}

function ChartCard({ card, ctx }: { card: BoardCard; ctx: CardCtx }) {
  const sheetId = card.chartConfig?.sheetId || 0;
  const { data, loading, error } = useCachedFetch<{
    rankings: Record<string, Array<{
      name: string; primary_value: number | null; primary_display: string;
      facts: Array<{ label: string; value: string }>;
    }>>;
    niches: RichNiche[];
  }>(
    `sheets-comparison-${sheetId}`,
    () => axios.get(`${API}/sheets/${sheetId}/comparison`, {
      headers: { Authorization: `Bearer ${ctx.token}` },
    }).then(r => r.data),
  );

  // Per-chart filter / unit-type / building-focus state
  const [unitFilter, setUnitFilter] = useState<'all' | 'studio' | '1b1b' | '2b2b'>('all');
  const [yearFilter, setYearFilter] = useState<'all' | 'new' | 'old'>('all');
  const [expanded, setExpanded] = useState<string | null>(null);

  if (loading) return <CardChrome ctx={ctx}>Loading chart…</CardChrome>;
  if (error)   return <CardChrome ctx={ctx}>{error}</CardChrome>;
  if (!data)   return <CardChrome ctx={ctx}>No data.</CardChrome>;

  const kind = card.chartKind || 'rent-bar';
  const niches = data.niches || [];
  // Apply year filter (new = built ≥ 2018, old = before 2018)
  const filteredNiches = niches.filter(n => {
    if (yearFilter === 'all') return true;
    if (!n.newness) return true;
    return yearFilter === 'new' ? n.newness >= 2018 : n.newness < 2018;
  });

  // ── Chart: Rent comparison ──────────────────────────────────────────
  if (kind === 'rent-bar') {
    // Use the selected unit type's rent, or fall back to price_unit
    const rentForNiche = (n: RichNiche): number | null => {
      if (unitFilter === 'all') return n.price;
      if (unitFilter === 'studio') return n.rents?.studio ?? null;
      if (unitFilter === '1b1b')   return n.rents?.['1b1b'] ?? null;
      return n.rents?.['2b2b'] ?? null;
    };
    const items = filteredNiches
      .map(n => {
        const r = rentForNiche(n);
        if (r === null) return null;
        return { niche: n, value: r };
      })
      .filter((x): x is { niche: RichNiche; value: number } => x !== null)
      .sort((a, b) => a.value - b.value);
    return (
      <CardChrome ctx={ctx}
        title="Rent comparison"
        eyebrow="Monthly rent across your sheet"
        action={
          <UnitPicker value={unitFilter} onChange={setUnitFilter} ctx={ctx} />
        }
      >
        <FilterRow value={yearFilter} onChange={setYearFilter} ctx={ctx} />
        <RichBarChart items={items} ctx={ctx} unit="money"
          unitLabel={
            unitFilter === 'studio' ? 'Studio' :
            unitFilter === '1b1b'   ? '1B1B'   :
            unitFilter === '2b2b'   ? '2B2B'   :
                                       'typical'
          }
          expanded={expanded} onExpand={setExpanded} />
        <Legend ctx={ctx} text={`${items.length} buildings · sorted cheapest → most expensive`} />
      </CardChrome>
    );
  }

  // ── Chart: Amenity comparison ───────────────────────────────────────
  if (kind === 'amenity-bar') {
    const items = filteredNiches
      .filter(n => n.amenity !== null)
      .map(n => ({ niche: n, value: n.amenity as number }))
      .sort((a, b) => b.value - a.value);
    return (
      <CardChrome ctx={ctx}
        title="Amenity comparison"
        eyebrow="In-unit + building amenities counted"
      >
        <FilterRow value={yearFilter} onChange={setYearFilter} ctx={ctx} />
        <RichBarChart items={items} ctx={ctx} unit="items"
          expanded={expanded} onExpand={setExpanded} />
        <Legend ctx={ctx} text="Click a row to see the amenity list" />
      </CardChrome>
    );
  }

  // ── Chart: Rent vs amenities scatter ────────────────────────────────
  if (kind === 'rent-vs-sqft') {
    return (
      <CardChrome ctx={ctx}
        title="Rent vs amenities"
        eyebrow="Find the value sweet spot"
      >
        <FilterRow value={yearFilter} onChange={setYearFilter} ctx={ctx} />
        <RichScatterChart
          niches={filteredNiches.filter(n => n.price !== null && n.amenity !== null)}
          ctx={ctx}
          xKey="amenity" yKey="price"
          xLabel="amenities" yLabel="rent"
          unitFilter={unitFilter}
        />
        <Legend ctx={ctx}
          text="Top-right = premium · Bottom-right = best value · Top-left = overpriced"
        />
      </CardChrome>
    );
  }

  // ── Chart: Newest construction ──────────────────────────────────────
  if (kind === 'persona-rank') {
    const items = filteredNiches
      .filter(n => n.newness !== null)
      .map(n => ({ niche: n, value: n.newness as number }))
      .sort((a, b) => b.value - a.value);
    return (
      <CardChrome ctx={ctx}
        title="Newest construction"
        eyebrow="Year built"
      >
        <RichBarChart items={items} ctx={ctx} unit="year"
          expanded={expanded} onExpand={setExpanded} />
        <Legend ctx={ctx} text={`${items.length} buildings · sorted newest → oldest`} />
      </CardChrome>
    );
  }
  return <CardChrome ctx={ctx}>Unknown chart.</CardChrome>;
}

// ── Filter UI strip (year / unit type) ───────────────────────────────

function FilterRow({ value, onChange, ctx }: {
  value: 'all' | 'new' | 'old';
  onChange: (v: 'all' | 'new' | 'old') => void;
  ctx: CardCtx;
}) {
  return (
    <div style={{
      display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap',
    }}>
      {(['all', 'new', 'old'] as const).map(v => {
        const active = value === v;
        const label = v === 'all' ? 'All' : v === 'new' ? 'Built ≥ 2018' : 'Built < 2018';
        return (
          <button key={v}
            onClick={() => onChange(v)}
            onMouseDown={e => e.stopPropagation()}
            style={{
              background: active ? ctx.accent : 'transparent',
              color: active ? '#fff' : ctx.fg,
              border: `1px solid ${active ? ctx.accent : ctx.border}`,
              borderRadius: 12, padding: '2px 10px',
              fontSize: 9, fontWeight: 600, cursor: 'pointer',
              letterSpacing: '0.06em', textTransform: 'uppercase',
              fontFamily: 'inherit',
            }}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

function UnitPicker({ value, onChange, ctx }: {
  value: 'all' | 'studio' | '1b1b' | '2b2b';
  onChange: (v: 'all' | 'studio' | '1b1b' | '2b2b') => void;
  ctx: CardCtx;
}) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value as 'all' | 'studio' | '1b1b' | '2b2b')}
      onMouseDown={e => e.stopPropagation()}
      style={selStyle(ctx)}>
      <option value="all">Any unit</option>
      <option value="studio">Studio</option>
      <option value="1b1b">1B1B</option>
      <option value="2b2b">2B2B</option>
    </select>
  );
}

function Legend({ ctx, text }: { ctx: CardCtx; text: string }) {
  return (
    <div style={{
      marginTop: 8, paddingTop: 6, borderTop: `1px solid ${ctx.border}`,
      color: ctx.fgMute, fontSize: 9, fontWeight: 500,
      letterSpacing: '0.04em',
    }}>
      {text}
    </div>
  );
}

// ── Rich bar chart: hover + expandable rows + median line ────────────

function RichBarChart({ items, ctx, unit, unitLabel, expanded, onExpand }: {
  items: Array<{ niche: RichNiche; value: number }>;
  ctx: CardCtx;
  unit: 'money' | 'items' | 'year';
  unitLabel?: string;
  expanded: string | null;
  onExpand: (k: string | null) => void;
}) {
  if (items.length === 0) {
    return <div style={{ color: ctx.fgMute, fontSize: 11, padding: '14px 0' }}>
      No data after filters.
    </div>;
  }
  const max = Math.max(...items.map(r => r.value));
  const min = Math.min(...items.map(r => r.value));
  const span = max - min || 1;
  // Compute median for context line
  const sorted = items.map(i => i.value).sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];

  const fmt = (v: number) =>
    unit === 'money' ? `$${Math.round(v).toLocaleString()}` :
    unit === 'year'  ? Math.round(v).toString() :
                       Math.round(v).toString();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Median reference annotation */}
      <div style={{
        fontSize: 9, color: ctx.fgMute, fontWeight: 600,
        letterSpacing: '0.08em', marginBottom: 4,
      }}>
        median: <span style={{ color: ctx.fg }}>{fmt(median)}</span>
        {unitLabel ? <span style={{ marginLeft: 8 }}>· comparing {unitLabel}</span> : null}
      </div>
      {items.map((r, i) => {
        const isExpanded = expanded === r.niche.name;
        const pct = ((r.value - min) / span) * 100;
        const w = 30 + (pct * 0.7);
        const isAboveMedian = r.value > median;
        return (
          <div key={i}>
            <div
              onClick={(e) => {
                e.stopPropagation();
                onExpand(isExpanded ? null : r.niche.name);
              }}
              onMouseDown={e => e.stopPropagation()}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, height: 22,
                cursor: 'pointer',
                borderRadius: 6,
                paddingLeft: 4, paddingRight: 4,
                background: isExpanded ? (ctx.theme === 'light' ? '#F5F0E8' : '#252530') : 'transparent',
                transition: 'background 0.12s',
              }}>
              <div style={{
                width: 120, fontSize: 10, color: ctx.fg, fontWeight: 500,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {r.niche.name}
              </div>
              <div style={{
                flex: 1, height: 12,
                background: ctx.theme === 'light' ? '#F5F0E8' : '#252530',
                borderRadius: 6, position: 'relative', overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: `${w}%`,
                  background: `linear-gradient(90deg, ${ctx.accent}, ${ctx.accent}cc)`,
                  borderRadius: 6,
                  transition: 'width 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                }} />
              </div>
              <div style={{
                width: 70, textAlign: 'right',
                fontFamily: 'Fraunces, Georgia, serif',
                fontSize: 11, fontWeight: 700,
                color: isAboveMedian ? ctx.fg : ctx.accent,
              }}>
                {fmt(r.value)}
              </div>
            </div>
            {/* Expanded detail row */}
            {isExpanded && (
              <div style={{
                padding: '8px 12px',
                background: ctx.theme === 'light' ? '#FAFAF9' : '#1A1A20',
                border: `1px solid ${ctx.border}`,
                borderRadius: 8,
                margin: '2px 0 6px 0',
                fontSize: 11, color: ctx.fg, lineHeight: 1.55,
                animation: 'coastFadeIn 0.2s ease-out',
              }}>
                {r.niche.location && (
                  <div style={{ color: ctx.fgMute, fontSize: 10, marginBottom: 4 }}>
                    {r.niche.location}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 6 }}>
                  {r.niche.newness && <BadgeChip ctx={ctx} label="built" value={String(Math.round(r.niche.newness))} />}
                  {r.niche.value && <BadgeChip ctx={ctx} label="$/sqft" value={`$${r.niche.value.toFixed(2)}`} />}
                  {r.niche.amenity !== null && <BadgeChip ctx={ctx} label="amenities" value={String(r.niche.amenity)} />}
                  {r.niche.income_min && <BadgeChip ctx={ctx} label="income" value={`$${Math.round(r.niche.income_min / 1000)}k/yr`} />}
                </div>
                {(r.niche.bld_all && r.niche.bld_all.length > 0) && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{
                      color: ctx.accent, fontSize: 9, fontWeight: 700,
                      letterSpacing: '0.12em', textTransform: 'uppercase',
                      marginBottom: 4,
                    }}>
                      Community amenities ({r.niche.bld_all.length})
                    </div>
                    <div style={{
                      display: 'flex', flexWrap: 'wrap', gap: 4,
                    }}>
                      {r.niche.bld_all.map((a, ai) => (
                        <span key={ai} style={{
                          background: ctx.theme === 'light' ? '#F5F0E8' : '#252530',
                          color: ctx.fg, fontSize: 10, fontWeight: 500,
                          padding: '2px 8px', borderRadius: 10,
                          border: `1px solid ${ctx.border}`,
                        }}>
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {(r.niche.unit_all && r.niche.unit_all.length > 0) && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{
                      color: ctx.accent, fontSize: 9, fontWeight: 700,
                      letterSpacing: '0.12em', textTransform: 'uppercase',
                      marginBottom: 4,
                    }}>
                      In-unit amenities ({r.niche.unit_all.length})
                    </div>
                    <div style={{
                      display: 'flex', flexWrap: 'wrap', gap: 4,
                    }}>
                      {r.niche.unit_all.map((a, ai) => (
                        <span key={ai} style={{
                          background: ctx.theme === 'light' ? '#FAFAF9' : '#1A1A20',
                          color: ctx.fg, fontSize: 10, fontWeight: 500,
                          padding: '2px 8px', borderRadius: 10,
                          border: `1px solid ${ctx.border}`,
                        }}>
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function BadgeChip({ ctx, label, value }: { ctx: CardCtx; label: string; value: string }) {
  return (
    <div>
      <div style={{
        fontSize: 8, color: ctx.fgMute, fontWeight: 600,
        letterSpacing: '0.08em', textTransform: 'uppercase',
      }}>{label}</div>
      <div style={{
        fontFamily: 'Fraunces, Georgia, serif',
        fontSize: 12, fontWeight: 700, color: ctx.fg,
      }}>{value}</div>
    </div>
  );
}

// ── Rich scatter chart: tooltips, quadrants, labels, color by year ──

function RichScatterChart({ niches, ctx, xKey, yKey, xLabel, yLabel, unitFilter }: {
  niches: RichNiche[];
  ctx: CardCtx;
  xKey: 'amenity' | 'value' | 'newness';
  yKey: 'price' | 'value' | 'amenity';
  xLabel: string;
  yLabel: string;
  unitFilter: 'all' | 'studio' | '1b1b' | '2b2b';
}) {
  const [hover, setHover] = useState<RichNiche | null>(null);
  const [pinned, setPinned] = useState<RichNiche | null>(null);
  const showing = hover || pinned;

  if (niches.length === 0) {
    return <div style={{ color: ctx.fgMute, fontSize: 11, padding: '14px 0' }}>
      No data after filters.
    </div>;
  }

  const getX = (n: RichNiche) => n[xKey] as number;
  const getY = (n: RichNiche) => n[yKey] as number;

  const xs = niches.map(getX).filter(v => v !== null);
  const ys = niches.map(getY).filter(v => v !== null);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const sortedX = [...xs].sort((a, b) => a - b);
  const sortedY = [...ys].sort((a, b) => a - b);
  const medX = sortedX[Math.floor(sortedX.length / 2)];
  const medY = sortedY[Math.floor(sortedY.length / 2)];

  const w = 340, h = 240;
  const padL = 50, padR = 14, padT = 14, padB = 36;
  const plotW = w - padL - padR;
  const plotH = h - padT - padB;
  const sx = (x: number) => padL + ((x - minX) / (maxX - minX || 1)) * plotW;
  const sy = (y: number) => padT + plotH - ((y - minY) / (maxY - minY || 1)) * plotH;

  // Color by year — newer = gold, older = muted
  const yrMin = Math.min(...niches.map(n => n.newness || 2000));
  const yrMax = Math.max(...niches.map(n => n.newness || 2030));
  const colorFor = (n: RichNiche) => {
    if (!n.newness) return ctx.fgMute;
    const t = (n.newness - yrMin) / (yrMax - yrMin || 1);
    // Lerp from muted blue to accent gold
    if (t > 0.66) return ctx.accent;
    if (t > 0.33) return '#B8956A';
    return '#7A8CA0';
  };

  const fmtX = (v: number) =>
    xKey === 'value' ? `$${v.toFixed(2)}` : Math.round(v).toString();
  const fmtY = (v: number) =>
    yKey === 'price' ? `$${Math.round(v).toLocaleString()}` :
    yKey === 'value' ? `$${v.toFixed(2)}` :
                       Math.round(v).toString();

  return (
    <div style={{ position: 'relative' }}>
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
        {/* Grid: horizontal + vertical median dashed lines */}
        <line x1={sx(medX)} y1={padT} x2={sx(medX)} y2={padT + plotH}
          stroke={ctx.fgMute} strokeWidth="0.7" strokeDasharray="3,4" opacity="0.6" />
        <line x1={padL} y1={sy(medY)} x2={padL + plotW} y2={sy(medY)}
          stroke={ctx.fgMute} strokeWidth="0.7" strokeDasharray="3,4" opacity="0.6" />

        {/* Quadrant labels */}
        <text x={padL + plotW - 4} y={padT + 10} textAnchor="end"
          fill={ctx.fgMute} fontSize="8" letterSpacing="0.1em">
          PREMIUM
        </text>
        <text x={padL + 4} y={padT + 10} textAnchor="start"
          fill={ctx.fgMute} fontSize="8" letterSpacing="0.1em">
          OVERPRICED
        </text>
        <text x={padL + plotW - 4} y={padT + plotH - 4} textAnchor="end"
          fill={ctx.accent} fontSize="8" letterSpacing="0.1em" fontWeight="700">
          BEST VALUE
        </text>
        <text x={padL + 4} y={padT + plotH - 4} textAnchor="start"
          fill={ctx.fgMute} fontSize="8" letterSpacing="0.1em">
          BUDGET TIER
        </text>

        {/* Axes */}
        <line x1={padL} y1={padT} x2={padL} y2={padT + plotH}
          stroke={ctx.border} strokeWidth="1" />
        <line x1={padL} y1={padT + plotH} x2={padL + plotW} y2={padT + plotH}
          stroke={ctx.border} strokeWidth="1" />

        {/* Axis ticks: 3 evenly-spaced */}
        {[0, 0.5, 1].map((t, i) => {
          const xVal = minX + t * (maxX - minX);
          const yVal = minY + t * (maxY - minY);
          return (
            <g key={i}>
              <text x={sx(xVal)} y={padT + plotH + 13} textAnchor="middle"
                fill={ctx.fgMute} fontSize="9">{fmtX(xVal)}</text>
              <text x={padL - 6} y={sy(yVal) + 3} textAnchor="end"
                fill={ctx.fgMute} fontSize="9">{fmtY(yVal)}</text>
            </g>
          );
        })}

        {/* Axis titles */}
        <text x={padL + plotW / 2} y={h - 8} textAnchor="middle"
          fill={ctx.fgMute} fontSize="9" letterSpacing="0.1em">
          {xLabel.toUpperCase()} →
        </text>
        <text x={12} y={padT + plotH / 2} textAnchor="middle"
          fill={ctx.fgMute} fontSize="9" letterSpacing="0.1em"
          transform={`rotate(-90 12 ${padT + plotH / 2})`}>
          ↑ {yLabel.toUpperCase()}
        </text>

        {/* Points: name labels ONLY on hover/pin (drastically cleaner) */}
        {niches.map((n, i) => {
          const x = sx(getX(n)), y = sy(getY(n));
          const isHover = hover?.row_index === n.row_index;
          const isPinned = pinned?.row_index === n.row_index;
          const isShown = isHover || isPinned;
          const fill = colorFor(n);
          return (
            <g key={i} style={{ cursor: 'pointer' }}>
              <circle cx={x} cy={y} r={isShown ? 8 : 5}
                fill={fill}
                fillOpacity={isShown ? 1 : 0.78}
                stroke={ctx.surface}
                strokeWidth={isShown ? 2.5 : 1.5}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(null)}
                onClick={(e) => {
                  e.stopPropagation();
                  setPinned(isPinned ? null : n);
                }}
                style={{ transition: 'all 0.15s' }} />
              {/* Name label only when hovered or pinned; tiny font */}
              {isShown && (
                <g style={{ pointerEvents: 'none' }}>
                  {/* Backdrop for legibility against gridlines */}
                  <rect x={x + 9} y={y - 6} width={n.name.length * 5 + 8} height={11}
                    fill={ctx.surface} fillOpacity="0.92" rx="3" />
                  <text x={x + 13} y={y + 3} fontSize="9.5"
                    fill={ctx.fg} fontWeight="700"
                    style={{ fontFamily: 'inherit' }}>
                    {n.name}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>

      {/* Color legend */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        marginTop: 4, fontSize: 9, color: ctx.fgMute,
        flexWrap: 'wrap', letterSpacing: '0.06em',
      }}>
        <span style={{ fontWeight: 600 }}>YEAR:</span>
        <ColorSwatch color="#7A8CA0" label={`< ${yrMin + Math.round((yrMax - yrMin) / 3)}`} />
        <ColorSwatch color="#B8956A" label="mid" />
        <ColorSwatch color={ctx.accent} label={`≥ ${yrMin + Math.round(2 * (yrMax - yrMin) / 3)}`} />
        <span style={{ flex: 1 }} />
        <span style={{ color: ctx.fgMute }}>click a dot to pin · hover for detail</span>
      </div>

      {/* Tooltip / detail panel */}
      {showing && (
        <div
          onMouseDown={e => e.stopPropagation()}
          style={{
            marginTop: 10,
            padding: 12,
            background: ctx.theme === 'light' ? '#FAFAF9' : '#1A1A20',
            border: `1px solid ${ctx.border}`,
            borderLeft: `3px solid ${ctx.accent}`,
            borderRadius: 10,
            fontSize: 11, color: ctx.fg,
            animation: 'coastFadeIn 0.18s ease-out',
          }}>
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 8,
            marginBottom: 6,
          }}>
            <div style={{
              fontFamily: 'Fraunces, Georgia, serif', fontSize: 14, fontWeight: 800,
              color: ctx.fg, flex: 1,
            }}>
              {showing.name}
            </div>
            {pinned && (
              <button onClick={() => setPinned(null)}
                onMouseDown={e => e.stopPropagation()}
                style={{
                  background: 'transparent', border: `1px solid ${ctx.border}`,
                  borderRadius: 6, color: ctx.fgMute,
                  fontSize: 9, padding: '2px 8px', cursor: 'pointer',
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  fontFamily: 'inherit',
                }}>
                unpin
              </button>
            )}
          </div>
          {showing.location && (
            <div style={{ color: ctx.fgMute, fontSize: 10, marginBottom: 6 }}>
              {showing.location}
            </div>
          )}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
            {showing.price !== null && <BadgeChip ctx={ctx}
              label={`rent (${showing.price_unit || 'typical'})`}
              value={`$${Math.round(showing.price).toLocaleString()}/mo`} />}
            {showing.value !== null && showing.value !== undefined &&
              <BadgeChip ctx={ctx} label="$/sqft"
                value={`$${showing.value.toFixed(2)}`} />}
            {showing.newness !== null && <BadgeChip ctx={ctx} label="built"
              value={String(Math.round(showing.newness))} />}
            {showing.amenity !== null && <BadgeChip ctx={ctx} label="amenities"
              value={String(showing.amenity)} />}
            {showing.income_min !== null && <BadgeChip ctx={ctx} label="income req"
              value={`$${Math.round(showing.income_min / 1000)}k/yr`} />}
          </div>
          {/* Per-unit rents */}
          {showing.rents && (
            <div style={{
              display: 'flex', gap: 10, fontSize: 10, color: ctx.fgMute,
              marginBottom: 6, paddingTop: 6, borderTop: `1px solid ${ctx.border}55`,
            }}>
              {showing.rents.studio && <span>Studio <span style={{ color: ctx.fg }}>${Math.round(showing.rents.studio).toLocaleString()}</span></span>}
              {showing.rents['1b1b'] && <span>1B1B <span style={{ color: ctx.fg }}>${Math.round(showing.rents['1b1b']!).toLocaleString()}</span></span>}
              {showing.rents['2b2b'] && <span>2B2B <span style={{ color: ctx.fg }}>${Math.round(showing.rents['2b2b']!).toLocaleString()}</span></span>}
            </div>
          )}
          {showing.bld_sample && showing.bld_sample.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <span style={{ color: ctx.accent, fontSize: 9, fontWeight: 700, letterSpacing: '0.08em' }}>BUILDING: </span>
              <span style={{ color: ctx.fg, fontSize: 10 }}>
                {showing.bld_sample.slice(0, 5).join(', ')}
              </span>
            </div>
          )}
          {showing.unit_sample && showing.unit_sample.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <span style={{ color: ctx.accent, fontSize: 9, fontWeight: 700, letterSpacing: '0.08em' }}>IN-UNIT: </span>
              <span style={{ color: ctx.fg, fontSize: 10 }}>
                {showing.unit_sample.slice(0, 5).join(', ')}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ColorSwatch({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ width: 9, height: 9, borderRadius: 5, background: color, display: 'inline-block' }} />
      <span>{label}</span>
    </span>
  );
}

// ── Shared chrome (header + body) for all cards ──────────────────────

function CardChrome({ ctx, title, eyebrow, action, children }: {
  ctx: CardCtx;
  title?: string;
  eyebrow?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div style={{
      width: '100%', height: '100%', boxSizing: 'border-box',
      padding: 14, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      {(title || eyebrow || action) && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          marginBottom: 10,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {eyebrow && (
              <div style={{
                color: ctx.accent, fontSize: 9, fontWeight: 700,
                letterSpacing: '0.18em', textTransform: 'uppercase',
                marginBottom: 2,
              }}>{eyebrow}</div>
            )}
            {title && (
              <div style={{
                fontFamily: 'Fraunces, Georgia, serif',
                fontSize: 14, fontWeight: 700, color: ctx.fg,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{title}</div>
            )}
          </div>
          {action}
        </div>
      )}
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {children}
      </div>
    </div>
  );
}

function selStyle(ctx: CardCtx): React.CSSProperties {
  return {
    background: ctx.surface, color: ctx.fg,
    border: `1px solid ${ctx.border}`, borderRadius: 6,
    fontSize: 10, padding: '3px 6px',
    fontFamily: 'inherit',
  };
}

function SectionHeading({ text, color, fg }: { text: string; color: string; fg: string }) {
  return (
    <div style={{
      color: color, fontSize: 10, fontWeight: 700,
      letterSpacing: '0.18em', textTransform: 'uppercase',
      marginBottom: 8,
    }}>{text}</div>
  );
}

function PickerCard({ title, desc, fg, fgMute, border, onClick }: {
  title: string; desc: string; fg: string; fgMute: string; border: string;
  onClick: () => void;
}) {
  return (
    <button onClick={onClick}
      style={{
        background: 'transparent',
        border: `1px solid ${border}`,
        borderRadius: 12, padding: '14px 16px',
        textAlign: 'left', cursor: 'pointer',
        color: fg, transition: 'all 0.15s',
        fontFamily: 'inherit',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#C9A96E'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = border; }}>
      <div style={{
        fontFamily: 'Fraunces, Georgia, serif',
        fontSize: 14, fontWeight: 700, marginBottom: 3,
      }}>{title}</div>
      <div style={{ fontSize: 11, color: fgMute, fontWeight: 400, lineHeight: 1.4 }}>
        {desc}
      </div>
    </button>
  );
}

function pickLocalFile(
  accept: string,
  maxMB: number,
  onLoad: (dataUrl: string, fileName: string) => void,
  onError: (msg: string) => void,
) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  input.style.display = 'none';
  input.onchange = () => {
    const f = input.files?.[0];
    if (!f) return;
    if (f.size > maxMB * 1024 * 1024) {
      onError(`File is ${(f.size / 1024 / 1024).toFixed(1)}MB — limit is ${maxMB}MB (browser storage is small; host on a server for bigger files and use the URL option).`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result;
      if (typeof url === 'string') onLoad(url, f.name);
      else onError('Could not read file.');
    };
    reader.onerror = () => onError('Could not read file.');
    reader.readAsDataURL(f);
  };
  document.body.appendChild(input);
  input.click();
  setTimeout(() => document.body.removeChild(input), 1000);
}

function addCardAtCenter(
  template: Partial<BoardCard>,
  setState: React.Dispatch<React.SetStateAction<BoardState>>,
  state: BoardState,
  setAddSectionOpen: (b: boolean) => void,
  setSelected: (id: string) => void,
) {
  // Drop near top-left of current viewport (visible area)
  const x = 80 - state.viewport.tx / state.viewport.scale;
  const y = 100 - state.viewport.ty / state.viewport.scale;
  const c: BoardCard = {
    id: genId(),
    type: template.type || 'sticky',
    x, y, w: template.w || 240, h: template.h || 140,
    ...template,
  };
  setState(prev => ({ ...prev, cards: [...prev.cards, c] }));
  setAddSectionOpen(false);
  setSelected(c.id);
}

function fmtMetric(value: number, fmt: string): string {
  if (fmt === 'int_compact') {
    if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(1)}k`;
    return Math.round(value).toLocaleString();
  }
  if (fmt === 'money') {
    if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}k`;
    return `$${Math.round(value).toLocaleString()}`;
  }
  if (fmt === 'money_per_month') return `$${Math.round(value).toLocaleString()}/mo`;
  if (fmt === 'pct')     return `${value.toFixed(1)}%`;
  if (fmt === 'decimal') return value.toFixed(1);
  if (fmt === 'int' || fmt === 'int_signed') return Math.round(value).toLocaleString();
  return String(value);
}
